/**
 * Glass Configurator V8 — Core Module
 * Consolidated IIFE replacing V7's 5 separate files.
 * Modules: State, PricingEngine, WizardController, BottomSheetController,
 *          CheckoutController, ShareController, TradeMode, DispatchEstimator,
 *          GTM/Analytics, init.
 *
 * Loaded with defer attribute. Reads data from #gc8-data JSON script tag.
 * No ES6+ syntax — compatible with all browsers Shopify serves.
 */
(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════════
   * HELPERS
   * ═══════════════════════════════════════════════════════════════════════════ */

  function debounce(fn, ms) {
    var timer;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function capitalise(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).replace(/-/g, ' ');
  }

  /** Format a raw configurator value into user-friendly display text. */
  var DISPLAY_LABELS = {
    'upvc': 'uPVC', 'aluminium': 'Aluminium', 'timber': 'Timber',
    'window': 'Window', 'door': 'Door', 'skylight': 'Skylight', 'conservatory': 'Conservatory'
  };
  function formatValue(str) {
    if (!str) return '';
    if (DISPLAY_LABELS[str]) return DISPLAY_LABELS[str];
    // Convert handles like "low-e-double" → "Low-E Double"
    return str.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }).replace(/\bUpvc\b/g, 'uPVC').replace(/\bLow E\b/g, 'Low-E');
  }

  // Laminated-glass handles come from Shopify's `handleize` filter applied to
  // metaobject names like "6.4mm Laminated". `handleize` converts the decimal
  // dot to a hyphen, producing "6-4mm-laminated". The generic formatValue then
  // turns every hyphen into a space, leaving "6 4mm Laminated" — visibly wrong
  // on the checkout line item. This helper restores the decimal point in the
  // leading dimension token, then defers to the generic title-case pipeline.
  function formatLaminatedLabel(value) {
    if (!value || value === 'none') return formatValue(value);
    // Match handles whose first token is N-Nmm (e.g. 6-4mm, 5-4mm, 8-8mm, 11-1mm).
    // Only the FIRST hyphen between leading digits and the trailing "mm" becomes
    // a decimal — subsequent hyphens stay as word separators.
    var withDecimal = value.replace(/^(\d+)-(\d+)mm/, '$1.$2mm');
    return formatValue(withDecimal);
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function formatPrice(n) {
    return '\u00A3' + n.toFixed(2);
  }

  // Ollie's price list (Sheet rates, extras, minimum order) is ex VAT. Shopify
  // is set to "prices include tax", so everything is priced ex VAT and VAT is
  // added once, at the end of PricingEngine.calculate. Agreed 2026-10-08.
  var VAT_RATE = 0.2;
  // Postcode areas that get the lower local minimum order (Bristol).
  var LOCAL_POSTCODE_AREAS = ['BS'];
  var DEFAULT_MIN_PRICE = 90;
  var DEFAULT_MIN_PRICE_LOCAL = 45;

  // Facts Sheet v1 (8 Oct 2026), "Largest unit we deliver": "£15 surcharge for
  // any unit over 2000mm". Confirmed ex VAT (Phil, 8 Oct 2026), like every
  // other price-list figure, so the customer pays £18 per oversize unit.
  var OVERSIZE_SURCHARGE = 15;
  var OVERSIZE_THRESHOLD_MM = 2000;
  // Same sheet row: "2900mm long × 1500mm high". Read as longer side ≤ 2900
  // and shorter side ≤ 1500, so a 1500 × 2900 portrait unit is allowed too
  // (the configurator doesn't model which way round the unit travels).
  var MAX_DELIVERY_LONG_MM = 2900;
  var MAX_DELIVERY_SHORT_MM = 1500;
  // Facts Sheet v1, section 5 lead times.
  var LEAD_TIME_STANDARD = '5\u20137 working days';
  var LEAD_TIME_SPECIAL = '10\u201314 working days';
  // True once the site_facts metaobject supplied the minimum orders; they
  // then win over the product's configurator_pricing metafields.
  var MIN_PRICE_FROM_FACTS = false;
  var MIN_PRICE_LOCAL_FROM_FACTS = false;

  // The constants above are fallbacks. When the section passes the site_facts
  // metaobject (data.facts, from snippets/site-facts-json.liquid), its values
  // replace them. Bad or missing values keep the fallback.
  function applySiteFacts(facts) {
    if (!facts || typeof facts !== 'object') return;
    function num(v, min) {
      var n = typeof v === 'number' ? v : parseFloat(v);
      return (isFinite(n) && n >= min) ? n : null;
    }
    function text(v) {
      return (typeof v === 'string' && v.trim()) ? v.trim() : null;
    }
    var v;
    if ((v = num(facts.vat_rate, 0)) !== null && v < 1) VAT_RATE = v;
    if (Array.isArray(facts.local_postcode_areas)) {
      var areas = facts.local_postcode_areas
        .map(function (a) { return String(a || '').trim().toUpperCase(); })
        .filter(function (a) { return /^[A-Z]{1,2}$/.test(a); });
      if (areas.length) LOCAL_POSTCODE_AREAS = areas;
    }
    if ((v = num(facts.min_order_ex_vat, 0)) !== null) { DEFAULT_MIN_PRICE = v; MIN_PRICE_FROM_FACTS = true; }
    if ((v = num(facts.min_order_local_ex_vat, 0)) !== null) { DEFAULT_MIN_PRICE_LOCAL = v; MIN_PRICE_LOCAL_FROM_FACTS = true; }
    if ((v = num(facts.oversize_surcharge_ex_vat, 0)) !== null) OVERSIZE_SURCHARGE = v;
    if ((v = num(facts.oversize_threshold_mm, 1)) !== null) OVERSIZE_THRESHOLD_MM = v;
    if ((v = num(facts.max_long_mm, 1)) !== null) MAX_DELIVERY_LONG_MM = v;
    if ((v = num(facts.max_short_mm, 1)) !== null) MAX_DELIVERY_SHORT_MM = v;
    if ((v = text(facts.lead_time_standard)) !== null) LEAD_TIME_STANDARD = v;
    if ((v = text(facts.lead_time_special)) !== null) LEAD_TIME_SPECIAL = v;
  }

  function withVat(n) {
    return Math.round(n * (1 + VAT_RATE) * 100) / 100;
  }

  function isOversizeUnit(width, height) {
    return width > OVERSIZE_THRESHOLD_MM || height > OVERSIZE_THRESHOLD_MM;
  }

  // Facts Sheet v1, section 5: standard units 5–7 working days to make and
  // dispatch; triple, toughened or laminated 10–14 working days.
  function leadTimeText(state) {
    if (exceedsDeliveryMax(state.width, state.height)) return 'Quote required';
    var special = state.construction === 'triple' ||
      String(state.glassType || '').toLowerCase().indexOf('triple') !== -1 ||
      !!state.toughened ||
      (state.laminated && state.laminated !== 'none');
    return special ? LEAD_TIME_SPECIAL : LEAD_TIME_STANDARD;
  }

  function exceedsDeliveryMax(width, height) {
    var w = Number(width) || 0;
    var h = Number(height) || 0;
    return Math.max(w, h) > MAX_DELIVERY_LONG_MM || Math.min(w, h) > MAX_DELIVERY_SHORT_MM;
  }

  function isLocalPostcode(parsed) {
    return !!(parsed && LOCAL_POSTCODE_AREAS.indexOf(parsed.area) !== -1);
  }

  /* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
   * UK POSTCODE \u2014 bind to the standalone module loaded by the lazy loader.
   *
   * Helpers + boundary list live in assets/configurator-v8-postcode.js so
   * the Node test suite can import them in isolation (single source of truth
   * for the mainland-zone exclusion list). Module is loaded via UMD wrapper \u2014
   * sets window.PR_POSTCODE in the browser, exports via module.exports for
   * tests.
   *
   * Inline fallback below: should never fire in production (the lazy loader
   * appends postcode.js before core.js and all three use `defer`, which
   * preserves DOM order). The fallback exists purely to keep Buy Now working
   * if a CDN edge serves a stale theme manifest mid-deploy that omits the
   * postcode asset.
   * \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */

  var _FALLBACK = (function () {
    var UK_POSTCODE_RE = /^([A-Z]{1,2}[0-9][A-Z0-9]?)\s*([0-9][A-Z]{2})$/i;
    var NON_MAINLAND_AREA_PREFIXES = ['BT', 'GY', 'JE', 'IM', 'IV', 'HS', 'KW', 'ZE'];
    var NON_MAINLAND_NUMERIC_RANGES = {
      PA: [[20, 78]], PH: [[17, 50]], KA: [[27, 28]], TR: [[21, 25]]
    };
    function parseUkPostcode(raw) {
      if (!raw) return null;
      var trimmed = String(raw).trim().toUpperCase();
      var match = trimmed.match(UK_POSTCODE_RE);
      if (!match) return null;
      var outward = match[1];
      var areaMatch = outward.match(/^([A-Z]{1,2})([0-9][A-Z0-9]?)$/);
      if (!areaMatch) return null;
      return {
        formatted: outward + ' ' + match[2],
        outward: outward,
        area: areaMatch[1],
        districtRaw: areaMatch[2],
        districtNumber: parseInt(areaMatch[2], 10)
      };
    }
    function isUkMainlandPostcode(parsed) {
      if (!parsed) return false;
      if (NON_MAINLAND_AREA_PREFIXES.indexOf(parsed.area) !== -1) return false;
      var ranges = NON_MAINLAND_NUMERIC_RANGES[parsed.area];
      if (ranges && !isNaN(parsed.districtNumber)) {
        for (var i = 0; i < ranges.length; i++) {
          if (parsed.districtNumber >= ranges[i][0] && parsed.districtNumber <= ranges[i][1]) return false;
        }
      }
      return true;
    }
    // MUST mirror BRISTOL_DELIVERY_ALLOWLIST in configurator-v8-postcode.js.
    // Fallback-only copy (fires only if the postcode asset is missing mid-deploy).
    // ~2 hours of Bristol (2026-08-03).
    var BRISTOL_DELIVERY_ALLOWLIST = {
      full_area_prefixes: ['BS', 'BA', 'NP', 'CF', 'SN', 'GL', 'TA', 'WR', 'HR',
        'SP', 'OX', 'DT', 'RG', 'B', 'SL', 'WV', 'CV', 'TQ', 'SO'],
      district_ranges: { EX: [[1, 17]], SA: [[1, 15]] }
    };
    function isWithinBristolDeliveryZone(parsed) {
      if (!parsed) return false;
      if (BRISTOL_DELIVERY_ALLOWLIST.full_area_prefixes.indexOf(parsed.area) !== -1) return true;
      var r = BRISTOL_DELIVERY_ALLOWLIST.district_ranges[parsed.area];
      if (r && !isNaN(parsed.districtNumber)) {
        for (var j = 0; j < r.length; j++) {
          if (parsed.districtNumber >= r[j][0] && parsed.districtNumber <= r[j][1]) return true;
        }
      }
      return false;
    }
    return {
      parseUkPostcode: parseUkPostcode,
      isUkMainlandPostcode: isUkMainlandPostcode,
      isWithinBristolDeliveryZone: isWithinBristolDeliveryZone
    };
  }());

  var _POSTCODE = (typeof window !== 'undefined' && window.PR_POSTCODE) || _FALLBACK;

  // Prefer the loaded module, but resolve PER METHOD with the inline fallback.
  // A stale cached postcode.js that predates a helper (e.g. the delivery-zone
  // predicate) is present-but-old: window.PR_POSTCODE is truthy yet missing
  // the method. Per-method fallback keeps the Buy Now gate fully correct during
  // that mid-deploy manifest skew, instead of degrading the whole store to
  // quote-only. Both copies are kept identical by a parity test.
  var parseUkPostcode = _POSTCODE.parseUkPostcode || _FALLBACK.parseUkPostcode;
  var isUkMainlandPostcode = _POSTCODE.isUkMainlandPostcode || _FALLBACK.isUkMainlandPostcode;
  var isWithinBristolDeliveryZone = _POSTCODE.isWithinBristolDeliveryZone || _FALLBACK.isWithinBristolDeliveryZone;

  /** Build the buyer-facing line-item title shown on the Shopify checkout.
   *  Replaces the legacy "Custom Glass Configuration" string with something
   *  descriptive enough that a buyer reviewing their order can verify what
   *  they're paying for without expanding the properties list. */
  /** Unit name for the invoice line and lead email. /pages/configure sells
   *  every unit through one carrier product titled "Double Glazed Unit", so a
   *  triple-glazed build takes its name from the configured construction. */
  function unitLabel(productTitle, state) {
    var title = productTitle || 'Custom Glass Unit';
    if (state.construction !== 'triple' || /triple/i.test(title)) return title;
    return /double/i.test(title) ? title.replace(/double/i, 'Triple') : 'Triple Glazed Unit';
  }

  function buildDisplayTitle(productTitle, state) {
    var base = unitLabel(productTitle, state);
    var dims = (state.width || '?') + '\u00D7' + (state.height || '?') + 'mm';
    var glass = formatValue(state.glassType) || 'Standard Double Glazed';
    var parts = [base, dims, glass];
    if (state.laminated && state.laminated !== 'none') {
      parts.push(formatLaminatedLabel(state.laminated));
    }
    return parts.join(' \u2014 ');
  }

  /* \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
   * SHEET PRICE LIST \u2014 the "Glass Prices" Google Sheet is the single source
   * of truth. data.priceList is the raw metaobject JSON:
   *   { rows: [ { kind, label, non_toughened, toughened }, ... ] }
   * We index it by build (4mm / 6mm / laminated) \u2192 normalised finish-combo
   * label \u2192 { nonT, T }, then derive the row from the customer's glass +
   * privacy + laminated + toughened selections. Edit the Sheet, re-sync,
   * prices update \u2014 no drift, no code change.
   * \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */

  function _norm(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim();
  }

  // Canonical label form for Sheet matching — resilient to the small text
  // variations a spreadsheet author introduces: spaces around slashes,
  // hyphens vs spaces, and "soft coat" vs "softcoat". Used on BOTH sides of
  // every Sheet lookup so a typo like "Lam / Soft-Coat" still matches.
  function _canon(s) {
    return _norm(s)
      .replace(/\s*\/\s*/g, '/')   // "lam / clear" -> "lam/clear"
      .replace(/-/g, ' ')           // hyphens -> spaces
      .replace(/soft\s*coat/g, 'softcoat')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function buildSheetIndex(priceList) {
    // Liquid may emit the metaobject json field as an object or a JSON string.
    if (typeof priceList === 'string') {
      try { priceList = JSON.parse(priceList); } catch (e) { return null; }
    }
    if (!priceList || !priceList.rows || !priceList.rows.length) return null;
    var index = { '4mm': {}, '6mm': {}, 'laminated': {} };
    var build = null;
    for (var i = 0; i < priceList.rows.length; i++) {
      var row = priceList.rows[i];
      var label = _norm(row.label);
      if (row.kind === 'section') {
        if (label.indexOf('laminated') !== -1 || label.indexOf('6.4') !== -1) build = 'laminated';
        else if (label.indexOf('6mm') !== -1) build = '6mm';
        else if (label.indexOf('4mm') !== -1) build = '4mm';
        else build = null;
        continue;
      }
      if (row.kind === 'rate' && build && index[build]) {
        var nonT = parseFloat(row.non_toughened);
        var t = parseFloat(row.toughened);
        index[build][_canon(row.label)] = {
          nonT: isNaN(nonT) ? null : nonT,
          T: isNaN(t) ? null : t
        };
      }
    }
    return index;
  }

  // Clear / Pattern / Satin from the obscure-glass selection. A satin-type
  // design \u2192 'satin' rate; any other obscure design \u2192 'pattern' rate.
  function _privacyFinish(privacyGlass) {
    var p = _norm(privacyGlass);
    if (!p || p === 'none' || p === 'clear') return 'clear';
    if (p.indexOf('satin') !== -1) return 'satin';
    return 'pattern';
  }

  // Returns { rate, label, build } for the current selection, or null to fall
  // back to the legacy base_price_sqm engine.
  function deriveSheetRate(state, data) {
    if (!data) return null;
    var index = data._sheetIndex;
    if (index === undefined) {
      index = buildSheetIndex(data.priceList);
      data._sheetIndex = index; // memoise (null if no list)
    }
    if (!index) return null;

    var glass = _norm(state.glassType);
    var laminatedSel = _norm(state.laminated);

    var build;
    if (laminatedSel && laminatedSel !== 'none') build = 'laminated';
    else if (glass.indexOf('6mm') !== -1) build = '6mm';
    else build = '4mm';

    var coating = (glass.indexOf('soft') !== -1 || glass.indexOf('low-e') !== -1 || glass.indexOf('lowe') !== -1) ? 'softcoat' : 'clear';
    var finish = _privacyFinish(state.privacyGlass);

    var label;
    if (build === 'laminated') {
      if (finish === 'pattern') label = 'lam/patterned';
      else if (finish === 'satin') label = 'lam/satin';
      else if (coating === 'softcoat') label = 'lam/soft coat';
      else label = 'lam/clear';
    } else {
      var first = finish === 'clear' ? 'clear' : finish; // clear | pattern | satin
      var second = coating === 'softcoat' ? (first === 'clear' ? 'soft coat' : 'softcoat') : 'clear';
      label = first + '/' + second;
    }

    var rows = index[build] || {};
    var hit = rows[_canon(label)];
    if (!hit) {
      // Visible signal: a Sheet miss silently falls back to the legacy £/m²
      // engine (different price). Surface it so a bad/edited Sheet is noticed.
      if (window.console && console.warn) {
        console.warn('[configurator] no Sheet rate for build="' + build + '" label="' + label + '" (canon="' + _canon(label) + '") — falling back to legacy pricing');
      }
      return null;
    }
    var rate = state.toughened ? hit.T : hit.nonT;
    if (rate == null) rate = (hit.nonT != null) ? hit.nonT : hit.T;
    if (rate == null) return null;
    return { rate: rate, label: label, build: build };
  }

  // Read a Sheet supplement rate (kind 'single'/'extra') by name fragment,
  // e.g. 'argon'. Returns the non_toughened number or null. Lets supplements
  // stay Sheet-driven instead of hardcoded.
  function sheetExtraRate(data, nameMatch) {
    if (!data) return null;
    var pl = data.priceList;
    if (typeof pl === 'string') { try { pl = JSON.parse(pl); } catch (e) { return null; } }
    if (!pl || !pl.rows) return null;
    nameMatch = _norm(nameMatch);
    for (var i = 0; i < pl.rows.length; i++) {
      var r = pl.rows[i];
      if ((r.kind === 'single' || r.kind === 'extra') && _norm(r.label).indexOf(nameMatch) !== -1) {
        var v = parseFloat(r.non_toughened);
        return isNaN(v) ? null : v;
      }
    }
    return null;
  }

  /* TRIPLE GLAZING — to Pane Relief a triple unit is the same double-glazed spec
   * with one extra pane in the middle. So we keep the double-glazed per-m² rate
   * (deriveSheetRate) exactly as-is and ADD a triple supplement per m² on top.
   * The supplement lives in the "Triple Units" group of the Glass Prices Sheet.
   * NOTE: the Railway sync currently tags the "Triple Units" header as a 'rate'
   * row rather than a 'section', so we locate the group by its label (resilient)
   * and read the rate rows beneath it, keyed by finish, until the next group.
   */
  function _tripleRows(data) {
    if (!data) return null;
    var pl = data.priceList;
    if (typeof pl === 'string') { try { pl = JSON.parse(pl); } catch (e) { return null; } }
    if (!pl || !pl.rows) return null;
    var out = {};
    var inTriple = false;
    for (var i = 0; i < pl.rows.length; i++) {
      var r = pl.rows[i];
      var label = _norm(r.label);
      var nonT = parseFloat(r.non_toughened);
      var t = parseFloat(r.toughened);
      var isHeader = label.indexOf('triple') !== -1 && isNaN(nonT) && isNaN(t);
      if (!inTriple) {
        // Only a row that NAMES triple but carries no numeric rate is the group
        // header — so a priced double row that merely mentions "triple" can't
        // hijack the scan (the Railway sync mis-tags this header as kind:rate).
        if (isHeader) inTriple = true;
        continue;
      }
      if (r.kind === 'section' || r.kind === 'title') break; // next group
      if (isHeader) continue; // ignore a repeated header
      if (r.kind === 'rate') {
        out[_canon(r.label)] = { nonT: isNaN(nonT) ? null : nonT, T: isNaN(t) ? null : t };
      }
    }
    return Object.keys(out).length ? out : null;
  }

  // True once the Sheet has at least one usable (priced) triple rate. Used to
  // gate the Double/Triple toggle so it stays hidden until prices are filled in.
  function hasTriplePricing(data) {
    var rows = _tripleRows(data);
    if (!rows) return false;
    for (var k in rows) {
      if (rows[k].nonT != null || rows[k].T != null) return true;
    }
    return false;
  }

  // Per-m² triple supplement for the current selection, or null if none.
  function tripleSupplementRate(state, data) {
    var rows = _tripleRows(data);
    if (!rows) return null;
    var glass = _norm(state.glassType);
    var coating = (glass.indexOf('soft') !== -1 || glass.indexOf('low-e') !== -1 || glass.indexOf('lowe') !== -1) ? 'soft coat' : 'clear';
    var finish = _privacyFinish(state.privacyGlass); // clear | pattern | satin
    var key = finish !== 'clear' ? finish : coating;
    var hit = rows[_canon(key)] || rows['clear'] || rows[Object.keys(rows)[0]];
    if (!hit) return null;
    var rate = state.toughened ? hit.T : hit.nonT;
    if (rate == null) rate = (hit.nonT != null) ? hit.nonT : hit.T;
    return (rate == null || isNaN(rate)) ? null : rate;
  }

  /* GA4 attribution — capture the GA4 client_id + session_id so Railway can fire
   * an attributed Measurement Protocol `purchase` when the configurator draft
   * order is paid. Uses the supported gtag('get', ...) API (works even when
   * Consent Mode runs cookieless / there's no readable _ga cookie, as on this
   * store). Primed early on load and cached; falls back to the _ga cookie, then
   * nulls — and when null, Railway uses a synthetic client_id so revenue still
   * records (unattributed). */
  var GA4_MEASUREMENT_ID = 'G-NVJ59FYSBD';
  var _ga4Attr = { client_id: null, session_id: null };

  function _ga4MeasurementId() {
    try {
      var g = window.google_tag_manager || {};
      for (var k in g) { if (g.hasOwnProperty(k) && k.indexOf('G-') === 0) return k; }
    } catch (e) { /* fall through */ }
    return GA4_MEASUREMENT_ID;
  }

  function primeGa4Attribution() {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    var id = _ga4MeasurementId();
    try {
      window.gtag('get', id, 'client_id', function (v) { if (v) _ga4Attr.client_id = String(v); });
      window.gtag('get', id, 'session_id', function (v) { if (v) _ga4Attr.session_id = String(v); });
    } catch (e) { /* gtag not ready yet — a later prime will catch it */ }
  }

  function _ga4FromCookie() {
    var out = { client_id: null, session_id: null };
    if (typeof document === 'undefined' || !document.cookie) return out;
    var cookies = document.cookie.split('; ');
    for (var i = 0; i < cookies.length; i++) {
      var eq = cookies[i].indexOf('=');
      if (eq < 0) continue;
      var name = cookies[i].slice(0, eq);
      var val = decodeURIComponent(cookies[i].slice(eq + 1));
      if (name === '_ga') {
        var p = val.split('.'); // GA1.1.<cid1>.<cid2>
        if (p.length >= 4) out.client_id = p[2] + '.' + p[3];
      } else if (/^_ga_[A-Z0-9]+$/.test(name) && !out.session_id) {
        var s = val.split('.'); // GS1.1.<session_id>.<n>...
        if (s.length >= 3) out.session_id = s[2];
      }
    }
    return out;
  }

  function readGa4Attribution() {
    primeGa4Attribution(); // opportunistic refresh at read time
    if (_ga4Attr.client_id) {
      return { client_id: _ga4Attr.client_id, session_id: _ga4Attr.session_id };
    }
    return _ga4FromCookie();
  }

  /* First-party traffic-source stash written sitewide by snippets/attribution-capture.liquid.
   * GA4 files these off-session configurator purchases as (not set), so we carry the
   * real source on the order itself. Best-effort — null when unavailable. */
  function readAttribution() {
    try {
      var raw = (window.localStorage && localStorage.getItem('pr_attribution')) || null;
      if (!raw) return null;
      var a = JSON.parse(raw);
      return (a && a.source) ? a : null;
    } catch (e) { return null; }
  }

  function attachAttribution(payload) {
    var a = readAttribution();
    if (!a) return;
    payload.attribution = {
      source: a.source, medium: a.medium, campaign: a.campaign, term: a.term,
      content: a.content, gclid: a.gclid, referrer: a.referrer,
      landing_page: a.landing_page, first_touch: a.first_source
    };
  }

  // Prime early so the client_id is cached by the time the buyer checks out.
  if (typeof window !== 'undefined') {
    var _ga4PrimeDelays = [800, 2500, 6000];
    for (var _gp = 0; _gp < _ga4PrimeDelays.length; _gp++) {
      setTimeout(primeGa4Attribution, _ga4PrimeDelays[_gp]);
    }
  }

  /** Grab a JPEG snapshot of the configurator canvas as a base64 data URL.
   *  Ops-only \u2014 sent as `configuration_preview` payload and stored by Railway
   *  as a `_configuration_preview` line-item property. NEVER surfaced to the
   *  customer. Quality 0.5 keeps the payload <100KB even on a 1200px canvas;
   *  ops only need legibility for fulfilment, not archival fidelity.
   *
   *  Returns null when the canvas isn't present or isn't on screen: below
   *  1024px the preview panel is hidden, and a hidden canvas exports as the
   *  empty "data:," (or a blank frame), which Railway's upload rejects. */
  function captureCanvasSnapshot() {
    try {
      var canvas = document.getElementById('gc8-canvas');
      if (!canvas || typeof canvas.toDataURL !== 'function') return null;
      var rect = canvas.getBoundingClientRect();
      if (!canvas.width || !canvas.height || rect.width < 1 || rect.height < 1) return null;
      var dataUrl = canvas.toDataURL('image/jpeg', 0.5);
      return /^data:image\/[a-z]+;base64,./.test(dataUrl) ? dataUrl : null;
    } catch (e) {
      // Common cause: tainted canvas from cross-origin background imagery.
      // Snapshot is non-essential, so swallow.
      return null;
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * RAILWAY API RELIABILITY
   * Retry with exponential backoff + localStorage persistence for failed quotes.
   * ═══════════════════════════════════════════════════════════════════════════ */

  var RETRY_ATTEMPTS = 2;
  var RETRY_BASE_MS = 1000;
  var PENDING_KEY = 'gc8_pending_quotes';

  var _apiKey = null;

  function setApiKey(key) { _apiKey = key; }

  function fetchWithRetry(url, payload, attempt) {
    attempt = attempt || 0;
    var headers = { 'Content-Type': 'application/json' };
    if (_apiKey) headers['X-API-Key'] = _apiKey;
    return fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (res.ok) return res.json();
      // Attach status + parsed body so callers can branch (e.g. a 422
      // out_of_area from the server delivery-zone gate -> route to quote).
      // Parse defensively; an error body may be empty or non-JSON.
      return res.json().catch(function () { return {}; }).then(function (body) {
        var e = new Error('HTTP ' + res.status);
        e.status = res.status;
        e.body = body || {};
        throw e;
      });
    }).catch(function (err) {
      // Retry transient failures: network errors (no status), 5xx, and the
      // retryable 4xx (429 rate-limit, 408/425 timeouts) an edge/CDN can emit
      // during a burst. Other 4xx (400/401/422) won't succeed on retry and may
      // carry an actionable body (e.g. 422 out_of_area) — surface immediately.
      var retryable4xx = err && (err.status === 429 || err.status === 408 || err.status === 425);
      var transient = !err || !err.status || err.status >= 500 || retryable4xx;
      if (transient && attempt < RETRY_ATTEMPTS) {
        return new Promise(function (resolve) {
          setTimeout(resolve, RETRY_BASE_MS * (attempt + 1));
        }).then(function () {
          return fetchWithRetry(url, payload, attempt + 1);
        });
      }
      throw err;
    });
  }

  /** Save a failed quote payload to localStorage for later retry. */
  function persistQuote(payload, apiUrl) {
    try {
      var pending = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
      pending.push({ payload: payload, url: apiUrl, ts: Date.now() });
      // Keep max 10 pending quotes, discard oldest
      if (pending.length > 10) pending = pending.slice(-10);
      localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    } catch (e) { /* localStorage unavailable — fail silently */ }
  }

  /** Attempt to flush any saved quotes on page load. */
  function flushPendingQuotes() {
    try {
      var pending = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
      if (!pending.length) return;
      // Discard quotes older than 7 days
      var cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      pending = pending.filter(function (q) { return q.ts > cutoff; });
      if (!pending.length) {
        localStorage.removeItem(PENDING_KEY);
        return;
      }
      var remaining = [];
      var flushing = pending.map(function (q) {
        return fetchWithRetry(q.url, q.payload).catch(function () {
          remaining.push(q); // Still failing, keep it
        });
      });
      Promise.all(flushing).then(function () {
        if (remaining.length) {
          localStorage.setItem(PENDING_KEY, JSON.stringify(remaining));
        } else {
          localStorage.removeItem(PENDING_KEY);
        }
      });
    } catch (e) { /* fail silently */ }
  }

  /** Read a data-gc8-field element's current value (radio, checkbox, select, input). */
  function readField(fieldName) {
    var el = document.querySelector('[data-gc8-field="' + fieldName + '"]');
    if (!el) return undefined;
    if (el.type === 'checkbox') return el.checked;
    if (el.type === 'radio') {
      var checked = document.querySelector('[data-gc8-field="' + fieldName + '"]:checked');
      return checked ? checked.value : undefined;
    }
    if (el.tagName === 'SELECT') return el.value;
    if (el.type === 'number') return parseFloat(el.value) || 0;
    return el.value;
  }

  /** Read numeric data-* attribute from the checked radio/selected option for a field. */
  function readFieldAttr(fieldName, attr) {
    var el = document.querySelector('[data-gc8-field="' + fieldName + '"]:checked');
    if (!el) {
      el = document.querySelector('[data-gc8-field="' + fieldName + '"]');
      if (el && el.tagName === 'SELECT' && el.selectedOptions.length) {
        el = el.selectedOptions[0];
      }
    }
    if (!el) return 0;
    return parseFloat(el.getAttribute(attr)) || 0;
  }

  /** Announce to screen readers via live region. */
  function announce(text) {
    var el = document.getElementById('gc8-sr-announce');
    if (el) el.textContent = text;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * 1. CONFIGURATOR STATE
   * ═══════════════════════════════════════════════════════════════════════════ */

  var State = {
    _state: {},
    _subscribers: [],
    _data: null,

    init: function (data) {
      this._data = data;
      this._state = {
        installType: 'window',
        conservatoryType: '',
        frameMaterial: 'upvc',
        width: 1200,
        height: 1400,
        glassType: 'Standard',
        glassPrice: 0,
        outerPane: '4mm-clear',
        construction: 'double',
        performanceTier: '',
        performancePricePercent: 0,
        privacyGlass: 'none',
        privacyPrice: 0,
        cavityWidth: '20',
        cavityPrice: 0,
        spacerType: 'standard',
        spacerColour: 'silver',
        laminated: 'none',
        laminatedPriceSqm: 0,
        toughened: false,
        toughenedPercent: 15,
        solar: false,
        solarPercent: 20,
        argon: false,
        argonPercent: 15,
        selfclean: false,
        georgianEnabled: false,
        georgianColour: 'white',
        georgianWidth: '18',
        georgianAcross: 2,
        georgianDown: 2,
        georgianSpacingMode: 'equal',
        georgianVPositions: [],
        georgianHPositions: [],
        leadEnabled: false,
        leadPattern: 'square',
        catflap: false,
        catflapSize: 'medium',
        letterbox: false,
        specialRequests: '',
        photos: [],
        quantity: 1,
        step: 1
      };

      // Apply defaults from data layer
      if (data.glassTypes) {
        for (var i = 0; i < data.glassTypes.length; i++) {
          if (data.glassTypes[i].isDefault) {
            this._state.glassType = data.glassTypes[i].name;
            break;
          }
        }
      }
    },

    getState: function () {
      var copy = {};
      for (var k in this._state) {
        if (this._state.hasOwnProperty(k)) copy[k] = this._state[k];
      }
      return copy;
    },

    get: function (key) {
      return this._state[key];
    },

    setState: function (key, value) {
      if (this._state[key] === value) return;
      this._state[key] = value;
      this._notify(key, value);
    },

    setMany: function (obj) {
      var changed = false;
      for (var k in obj) {
        if (obj.hasOwnProperty(k) && this._state[k] !== obj[k]) {
          this._state[k] = obj[k];
          changed = true;
        }
      }
      if (changed) this._notify('bulk', null);
    },

    subscribe: function (callback) {
      this._subscribers.push(callback);
    },

    _notify: function (key, value) {
      for (var i = 0; i < this._subscribers.length; i++) {
        try { this._subscribers[i](key, value, this._state); } catch (e) { /* swallow */ }
      }
    },

    /* ── localStorage persistence ── */

    saveState: function () {
      if (!this._data || !this._data.product) return;
      var storageKey = 'gc8-' + this._data.product.handle;
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          config: this._state,
          ts: Date.now()
        }));
      } catch (e) { /* quota or private browsing */ }
    },

    loadState: function () {
      if (!this._data || !this._data.product) return false;
      var storageKey = 'gc8-' + this._data.product.handle;
      try {
        var raw = localStorage.getItem(storageKey);
        if (!raw) return false;
        var stored = JSON.parse(raw);
        var sevenDays = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - stored.ts > sevenDays) {
          localStorage.removeItem(storageKey);
          return false;
        }
        if (stored.config) {
          for (var k in stored.config) {
            if (stored.config.hasOwnProperty(k) && this._state.hasOwnProperty(k)) {
              this._state[k] = stored.config[k];
            }
          }
          return true;
        }
      } catch (e) { /* corrupt data */ }
      return false;
    }
  };


  /* ═══════════════════════════════════════════════════════════════════════════
   * 2. PRICING ENGINE
   * ═══════════════════════════════════════════════════════════════════════════ */

  var PricingEngine = {
    _lastPrice: 0,
    _animFrame: null,

    /**
     * Calculate price from current state + data layer.
     * Returns { unitPrice, unitPriceExVat, basePrice, extras, breakdown, variant, qty, total }.
     * unitPrice/total include VAT; basePrice, extras and breakdown values are ex VAT.
     * opts.local: delivery postcode is local (Bristol), so the lower minimum
     * applies. Unknown postcode prices at the standard (higher) minimum.
     */
    calculate: function (state, data, opts) {
      var pricing = data.pricing;
      var width = state.width || 1200;
      var height = state.height || 1400;
      var areaSqm = (width * height) / 1000000;

      var minPriceStandard = MIN_PRICE_FROM_FACTS ? DEFAULT_MIN_PRICE : (parseFloat(pricing.minPrice) || DEFAULT_MIN_PRICE);
      var minPriceLocal = MIN_PRICE_LOCAL_FROM_FACTS ? DEFAULT_MIN_PRICE_LOCAL : (parseFloat(pricing.minPriceLocal) || DEFAULT_MIN_PRICE_LOCAL);
      var isLocal = !!(opts && opts.local);
      var minPrice = isLocal ? minPriceLocal : minPriceStandard;

      // ── Base price ──
      // Preferred: price straight from the Sheet (single source of truth). The
      // Sheet per-m² rate already bakes in glass spec + finish + toughening, so
      // the legacy %-multipliers and flat glass modifiers are NOT layered on.
      var sheet = deriveSheetRate(state, data);
      var pricePerSqm, usingSheet = false;

      if (sheet) {
        usingSheet = true;
        pricePerSqm = sheet.rate;
      } else {
        // Legacy fallback — only when the Sheet has no rate for this selection.
        pricePerSqm = parseFloat(pricing.basePriceSqm) || 85;
        if (state.performancePricePercent > 0) pricePerSqm *= (1 + state.performancePricePercent / 100);
        if (state.toughened && state.toughenedPercent > 0) pricePerSqm *= (1 + state.toughenedPercent / 100);
        if (state.solar && state.solarPercent > 0) pricePerSqm *= (1 + state.solarPercent / 100);
        if (state.argon && state.argonPercent > 0) pricePerSqm *= (1 + state.argonPercent / 100);
      }

      var rawBase = areaSqm * pricePerSqm;
      var basePrice = Math.max(rawBase, minPrice);
      var floorHit = rawBase <= minPrice;

      // ── Extras (flat fees + per-item) ──
      var extras = 0;
      var breakdown = [];

      breakdown.push({
        label: usingSheet
          ? 'Glass (' + areaSqm.toFixed(2) + ' m\u00B2 \u00D7 \u00A3' + pricePerSqm.toFixed(0) + '/m\u00B2' + (sheet.build === 'laminated' ? ', laminated' : '') + ')'
          : 'Base (' + areaSqm.toFixed(2) + ' m\u00B2 \u00D7 \u00A3' + pricePerSqm.toFixed(0) + '/m\u00B2)',
        value: basePrice
      });

      // Legacy flat modifiers \u2014 skipped when pricing from the Sheet (the per-m\u00B2
      // rate already includes glass spec / finish / coating / toughening).
      if (!usingSheet) {

      // Glass type flat modifier
      if (state.glassPrice && state.glassPrice !== 0) {
        extras += state.glassPrice;
        breakdown.push({ label: 'Glass type: ' + state.glassType, value: state.glassPrice });
      }

      // Privacy glass flat
      if (state.privacyPrice && state.privacyPrice !== 0) {
        extras += state.privacyPrice;
        breakdown.push({ label: 'Privacy: ' + state.privacyGlass, value: state.privacyPrice });
      }

      // Cavity width flat
      if (state.cavityPrice && state.cavityPrice !== 0) {
        extras += state.cavityPrice;
        breakdown.push({ label: 'Cavity: ' + state.cavityWidth + 'mm', value: state.cavityPrice });
      }

      // Laminated glass (per sqm)
      if (state.laminated !== 'none' && state.laminatedPriceSqm > 0) {
        var lamCost = state.laminatedPriceSqm * areaSqm;
        extras += lamCost;
        breakdown.push({ label: 'Laminated: ' + formatLaminatedLabel(state.laminated), value: lamCost });
      }

      // Self-cleaning
      if (state.selfclean) {
        extras += pricing.selfcleanPrice;
        breakdown.push({ label: 'Self-cleaning coating', value: pricing.selfcleanPrice });
      }

      } // end !usingSheet legacy modifiers

      // Obscure-glass note (price is in the Sheet rate; shown for clarity).
      if (usingSheet && state.privacyGlass && state.privacyGlass !== 'none' && state.privacyGlass !== 'clear') {
        breakdown.push({ label: 'Obscure glass: ' + (formatValue(state.privacyGlass) || state.privacyGlass), value: 0, note: true });
      }

      // Argon gas fill — Sheet "Argon gas" rate, applied per m².
      if (usingSheet && state.argon) {
        var argonRate = sheetExtraRate(data, 'argon');
        if (argonRate == null) argonRate = 6;
        var argonCost = argonRate * areaSqm;
        extras += argonCost;
        breakdown.push({ label: 'Argon gas fill (£' + argonRate + '/m²)', value: argonCost });
      }

      // Triple glazing — a per-m² supplement added on top of the double-glazed
      // rate (a triple unit is the double spec plus one extra middle pane).
      // Honour BOTH the construction toggle AND a triple-named glass type, so
      // pricing can never disagree with the specs/canvas (which key off the glass
      // handle) — otherwise a "Triple" glass could price as double.
      var wantsTriple = state.construction === 'triple' || _norm(state.glassType).indexOf('triple') !== -1;
      if (usingSheet && wantsTriple) {
        var tripleRate = tripleSupplementRate(state, data);
        if (tripleRate != null && tripleRate > 0) {
          var tripleCost = tripleRate * areaSqm;
          extras += tripleCost;
          breakdown.push({ label: 'Triple glazing (£' + tripleRate + '/m²)', value: tripleCost });
        }
      }

      // Georgian bars
      if (state.georgianEnabled) {
        var totalBars = (state.georgianAcross - 1) + (state.georgianDown - 1);
        var barCost = totalBars * pricing.georgianBarPrice;
        extras += barCost;
        breakdown.push({ label: 'Georgian bars (' + totalBars + '\u00D7)', value: barCost });
      }

      // Catflap (legacy only — not in the Sheet)
      if (!usingSheet && state.catflap) {
        extras += pricing.catflapPrice;
        breakdown.push({ label: 'Catflap cutout', value: pricing.catflapPrice });
      }

      // Letterbox (legacy only — not in the Sheet)
      if (!usingSheet && state.letterbox) {
        extras += pricing.letterboxPrice;
        breakdown.push({ label: 'Letterbox cutout', value: pricing.letterboxPrice });
      }

      // Oversize surcharge — flat, per unit, ex VAT, on top of the minimum.
      var oversize = isOversizeUnit(width, height);
      if (oversize) {
        extras += OVERSIZE_SURCHARGE;
        breakdown.push({ label: 'Oversize unit (over ' + OVERSIZE_THRESHOLD_MM + 'mm)', value: OVERSIZE_SURCHARGE });
      }

      // Informational notes (price already in the rate / multiplier).
      if (state.toughened) {
        breakdown.push({
          label: usingSheet ? 'Toughened glass' : 'Toughened (+' + state.toughenedPercent + '%)',
          value: 0, note: true
        });
      }
      if (!usingSheet && state.solar) {
        breakdown.push({ label: 'Solar control (+' + state.solarPercent + '%)', value: 0, note: true });
      }
      if (!usingSheet && state.argon) {
        breakdown.push({ label: 'Argon gas (+' + state.argonPercent + '%)', value: 0, note: true });
      }

      // Round ex VAT first so the breakdown's ex-VAT + VAT rows add up to the
      // inc-VAT price to the penny.
      var unitPriceExVat = Math.round((basePrice + extras) * 100) / 100;
      var unitPrice = withVat(unitPriceExVat);
      var qty = state.quantity || 1;
      var total = unitPrice * qty;

      // ── Variant selection ──
      // Variants only supply the draft order's variant_id (the line price is
      // overridden); match on the ex-VAT price, as before VAT was added.
      var variant = this.selectVariant(unitPriceExVat, data);

      return {
        unitPrice: unitPrice,
        unitPriceExVat: unitPriceExVat,
        vat: unitPrice - unitPriceExVat,
        basePrice: basePrice,
        extras: extras,
        breakdown: breakdown,
        variant: variant,
        qty: qty,
        total: total,
        areaSqm: areaSqm,
        floorHit: floorHit,
        oversize: oversize,
        exceedsDeliveryMax: exceedsDeliveryMax(width, height),
        minPrice: minPrice,
        minPriceStandard: minPriceStandard,
        minPriceLocal: minPriceLocal,
        isLocal: isLocal
      };
    },

    selectVariant: function (calculatedPrice, data) {
      var variants = data.variants;
      if (!variants || variants.length === 0) return null;

      var sorted = variants.slice().sort(function (a, b) {
        return parseFloat(a.price) - parseFloat(b.price);
      });

      for (var i = 0; i < sorted.length; i++) {
        if (parseFloat(sorted[i].price) >= calculatedPrice) {
          return sorted[i];
        }
      }

      return sorted[sorted.length - 1];
    },

    /** Recalculate from current state, update DOM, dispatch event. */
    recalculate: function (data) {
      var state = State.getState();
      var result = this.calculate(state, data);

      // Update price display with animation
      this.animatePrice(result.unitPrice);

      // Update breakdown table
      this.updateBreakdown(result);

      // Update design subtotal
      this.updateDesignSubtotal(result, data);

      // Update area display
      var areaEl = document.getElementById('gc8-area-display');
      if (areaEl) areaEl.textContent = 'Area: ' + result.areaSqm.toFixed(2) + ' m\u00B2';

      // Floor-price notice (when raw area-priced base would be below minimum)
      var floorEl = document.getElementById('gc8-price-floor');
      if (floorEl) {
        if (result.floorHit) {
          floorEl.textContent = 'Minimum order ' + formatPrice(withVat(result.minPriceStandard)) +
            ' inc VAT (' + formatPrice(withVat(result.minPriceLocal)) + ' for Bristol BS postcodes)';
          floorEl.removeAttribute('hidden');
        } else {
          floorEl.setAttribute('hidden', '');
        }
      }

      // Dynamic canvas aria-label — describes current configuration to screen readers
      var canvasEl = document.getElementById('gc8-canvas');
      if (canvasEl) {
        var ariaState = State.getState();
        var ariaParts = [];
        ariaParts.push((ariaState.installType || 'window') + ' configuration');
        ariaParts.push(ariaState.width + ' by ' + ariaState.height + ' millimetres');
        ariaParts.push((ariaState.frameMaterial || 'upvc') + ' frame');
        if (ariaState.privacyGlass && ariaState.privacyGlass !== 'none') {
          ariaParts.push(ariaState.privacyGlass.replace(/-/g, ' ') + ' privacy glass');
        }
        if (ariaState.performanceTier) ariaParts.push(ariaState.performanceTier + ' performance');
        if (ariaState.toughened) ariaParts.push('toughened');
        if (ariaState.argon) ariaParts.push('argon-filled');
        if (ariaState.solar) ariaParts.push('solar control');
        if (ariaState.georgianEnabled) ariaParts.push('with Georgian bars');
        canvasEl.setAttribute('aria-label', 'Glass unit preview: ' + ariaParts.join(', '));
      }

      // Dispatch event for other modules
      document.dispatchEvent(new CustomEvent('gc8:price-update', {
        detail: result
      }));

      return result;
    },

    /** Price count-up animation (400ms ease-out cubic). */
    animatePrice: function (newPrice) {
      var priceEls = document.querySelectorAll('.gc8__price-value');
      if (!priceEls.length) return;

      // Switch label from "From" to "Your price" on first real calculation
      if (!this._labelSwitched) {
        var label = document.getElementById('gc8-price-label');
        if (label) label.textContent = 'Your price';
        this._labelSwitched = true;
      }

      if (newPrice === this._lastPrice) {
        for (var p = 0; p < priceEls.length; p++) priceEls[p].textContent = formatPrice(newPrice);
        return;
      }

      var oldPrice = this._lastPrice;
      this._lastPrice = newPrice;

      if (this._animFrame) cancelAnimationFrame(this._animFrame);

      var startTime = null;
      var duration = 400;
      var self = this;

      for (var p = 0; p < priceEls.length; p++) priceEls[p].classList.add('gc8__price-value--updating');

      function animate(timestamp) {
        if (!startTime) startTime = timestamp;
        var progress = Math.min((timestamp - startTime) / duration, 1);

        // Ease-out cubic
        var eased = 1 - Math.pow(1 - progress, 3);
        var current = oldPrice + (newPrice - oldPrice) * eased;
        var formatted = formatPrice(current);
        for (var p = 0; p < priceEls.length; p++) priceEls[p].textContent = formatted;

        if (progress < 1) {
          self._animFrame = requestAnimationFrame(animate);
        } else {
          var final = formatPrice(newPrice);
          for (var p = 0; p < priceEls.length; p++) {
            priceEls[p].textContent = final;
            priceEls[p].classList.remove('gc8__price-value--updating');
          }
        }
      }

      self._animFrame = requestAnimationFrame(animate);
    },

    /** Update the price breakdown table. */
    updateBreakdown: function (result) {
      var body = document.getElementById('gc8-price-breakdown');
      if (!body) return;

      var html = '';
      for (var i = 0; i < result.breakdown.length; i++) {
        var item = result.breakdown[i];
        if (item.note) {
          html += '<tr class="gc8__breakdown-note"><td>' + item.label + '</td><td>incl. in base</td></tr>';
        } else {
          html += '<tr><td>' + item.label + '</td><td>' + formatPrice(item.value) + '</td></tr>';
        }
      }
      html += '<tr><td>Subtotal (ex VAT)</td><td>' + formatPrice(result.unitPriceExVat) + '</td></tr>';
      html += '<tr><td>VAT (' + Math.round(VAT_RATE * 100) + '%)</td><td>' + formatPrice(result.vat) + '</td></tr>';
      html += '<tr class="gc8__breakdown-total"><td><strong>Unit price (inc VAT)</strong></td><td><strong>' + formatPrice(result.unitPrice) + '</strong></td></tr>';
      if (result.qty > 1) {
        html += '<tr class="gc8__breakdown-total"><td><strong>Total (' + result.qty + '\u00D7)</strong></td><td><strong>' + formatPrice(result.total) + '</strong></td></tr>';
      }
      body.innerHTML = html;
    },

    /** Update the inline design-extras subtotal. */
    updateDesignSubtotal: function (result, data) {
      var el = document.getElementById('gc8-design-subtotal');
      if (!el) return;

      var state = State.getState();
      var designTotal = 0;
      var pricing = data.pricing;

      if (state.georgianEnabled) {
        var bars = (state.georgianAcross - 1) + (state.georgianDown - 1);
        designTotal += bars * pricing.georgianBarPrice;
      }
      if (state.catflap) designTotal += pricing.catflapPrice;
      if (state.letterbox) designTotal += pricing.letterboxPrice;
      if (state.selfclean) designTotal += pricing.selfcleanPrice;

      if (designTotal > 0) {
        el.textContent = '+' + formatPrice(designTotal);
        el.style.display = 'inline';
      } else {
        el.style.display = 'none';
      }
    }
  };


  /* ═══════════════════════════════════════════════════════════════════════════
   * 3. WIZARD CONTROLLER
   * ═══════════════════════════════════════════════════════════════════════════ */

  var STEP_NAMES = ['Getting Started', 'Glass Selection', 'Performance & Upgrades', 'Design Options', 'Review & Order'];

  var WizardController = {
    _currentStep: 1,
    _totalSteps: 5,
    _data: null,

    init: function (data) {
      this._data = data;
      this.bindNav();
      this.bindValidation();
      this.goToStep(1);
    },

    getCurrentStep: function () {
      return this._currentStep;
    },

    goToStep: function (n) {
      if (n < 1 || n > this._totalSteps) return;

      // Validate before advancing forward
      if (n > this._currentStep && !this.validateStep(this._currentStep)) {
        return;
      }

      var steps = document.querySelectorAll('.gc8__step');
      for (var i = 0; i < steps.length; i++) {
        var stepNum = parseInt(steps[i].getAttribute('data-gc8-step'));
        if (stepNum === n) {
          steps[i].classList.add('gc8__step--active');
          steps[i].removeAttribute('hidden');
        } else {
          steps[i].classList.remove('gc8__step--active');
          steps[i].setAttribute('hidden', '');
        }
      }

      // Update progress segments
      var segs = document.querySelectorAll('.gc8__progress-seg');
      for (var j = 0; j < segs.length; j++) {
        var segNum = parseInt(segs[j].getAttribute('data-gc8-progress'));
        segs[j].classList.remove('gc8__progress-seg--active', 'gc8__progress-seg--done');
        if (segNum < n) {
          segs[j].classList.add('gc8__progress-seg--done');
        } else if (segNum === n) {
          segs[j].classList.add('gc8__progress-seg--active');
        }
      }

      this._currentStep = n;
      State.setState('step', n);

      // Update step indicator text
      var stepIndicator = document.getElementById('gc8-step-indicator');
      if (stepIndicator) stepIndicator.textContent = 'Step ' + n + ' of ' + this._totalSteps;

      // Update mobile sheet step text
      var sheetStep = document.getElementById('gc8-sheet-step');
      if (sheetStep) sheetStep.textContent = 'Step ' + n + ': ' + (STEP_NAMES[n - 1] || '');

      // Show/hide price bar buttons based on step
      var barNext = document.getElementById('gc8-bar-next');
      var cartBtn = document.getElementById('gc8-cart-btn');
      var quoteBtn = document.getElementById('gc8-quote-btn');
      var preferQuote = document.getElementById('gc8-prefer-quote');

      if (barNext) barNext.style.display = (n < this._totalSteps) ? '' : 'none';
      if (n === this._totalSteps) {
        // Show cart/quote on review step
        if (cartBtn) cartBtn.style.display = '';
        if (preferQuote) preferQuote.style.display = 'block';
        CheckoutController.updateCheckoutMode();
      } else {
        if (cartBtn) cartBtn.style.display = 'none';
        if (quoteBtn) quoteBtn.style.display = 'none';
        if (preferQuote) preferQuote.style.display = 'none';
      }

      // Mobile sheet nav visibility
      var sheetBack = document.getElementById('gc8-sheet-back');
      var sheetNext = document.getElementById('gc8-sheet-next');
      if (sheetBack) sheetBack.style.visibility = (n > 1) ? 'visible' : 'hidden';
      if (sheetNext) {
        if (n < this._totalSteps) {
          sheetNext.textContent = 'Next Step';
          sheetNext.style.display = '';
        } else {
          sheetNext.style.display = 'none';
        }
      }

      // Focus management — move focus to first visible focusable element in new step
      var activeStep = document.querySelector('.gc8__step[data-gc8-step="' + n + '"]');
      if (activeStep) {
        var focusable = activeStep.querySelector('input:not([type="hidden"]), select, textarea, button:not([style*="display: none"])');
        if (focusable && focusable.offsetParent !== null) {
          setTimeout(function () { focusable.focus(); }, 100);
        }
      }

      // Scroll to top of configurator
      var gc8 = document.querySelector('.gc8');
      if (gc8) gc8.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Dispatch step-change event for GTM
      document.dispatchEvent(new CustomEvent('gc8:step-change', {
        detail: { step: n, stepName: STEP_NAMES[n - 1] || '' }
      }));

      // ARIA announce
      announce('Step ' + n + ' of ' + this._totalSteps + ': ' + (STEP_NAMES[n - 1] || ''));

      // Update review table + tech specs on final step
      if (n === this._totalSteps) {
        this.populateReview();
        this.populateTechSpecs();
      }

      // Smart logic: auto-enable toughened for doors/skylights
      this.applyBusinessRules();

      // Update progress segment aria-selected
      var segs2 = document.querySelectorAll('.gc8__progress-seg');
      for (var k = 0; k < segs2.length; k++) {
        var segN = parseInt(segs2[k].getAttribute('data-gc8-progress'));
        segs2[k].setAttribute('aria-selected', segN === n ? 'true' : 'false');
      }
    },

    bindNav: function () {
      var self = this;

      // Next buttons
      var nextBtns = document.querySelectorAll('[data-gc8-nav="next"]');
      for (var i = 0; i < nextBtns.length; i++) {
        nextBtns[i].addEventListener('click', function () {
          self.goToStep(self._currentStep + 1);
        });
      }

      // Prev buttons
      var prevBtns = document.querySelectorAll('[data-gc8-nav="prev"]');
      for (var j = 0; j < prevBtns.length; j++) {
        prevBtns[j].addEventListener('click', function () {
          self.goToStep(self._currentStep - 1);
        });
      }

      // Progress segment clicks (jump to step)
      var segs = document.querySelectorAll('.gc8__progress-seg');
      for (var k = 0; k < segs.length; k++) {
        segs[k].addEventListener('click', function () {
          var target = parseInt(this.getAttribute('data-gc8-progress'));
          if (target <= self._currentStep) {
            self.goToStep(target);
          }
        });
      }
    },

    bindValidation: function () {
      var self = this;

      // Show/hide conservatory sub-type when installType changes
      var installFields = document.querySelectorAll('[data-gc8-field="installType"]');
      for (var i = 0; i < installFields.length; i++) {
        installFields[i].addEventListener('change', function () {
          var conservConfig = document.querySelector('.gc8__conservatory-config');
          if (conservConfig) {
            conservConfig.style.display = this.value === 'conservatory' ? 'block' : 'none';
          }
        });
      }
    },

    /** Validate the current step before allowing forward navigation. */
    validateStep: function (stepNum) {
      var state = State.getState();

      switch (stepNum) {
        case 1: // Installation + Dimensions
          var widthErr = document.getElementById('gc8-width-error');
          var heightErr = document.getElementById('gc8-height-error');
          if (widthErr) widthErr.textContent = '';
          if (heightErr) heightErr.textContent = '';

          if (!state.width || state.width < 150) {
            announce('Width must be at least 150mm.');
            if (widthErr) widthErr.textContent = 'Minimum width is 150mm';
            var wEl = document.getElementById('gc8-width');
            if (wEl) wEl.classList.add('pr-input--error');
            return false;
          }
          if (!state.height || state.height < 150) {
            announce('Height must be at least 150mm.');
            if (heightErr) heightErr.textContent = 'Minimum height is 150mm';
            var hEl = document.getElementById('gc8-height');
            if (hEl) hEl.classList.add('pr-input--error');
            return false;
          }
          if (state.width > (this._data.maxWidthMm || 4000)) {
            announce('Width exceeds maximum of ' + (this._data.maxWidthMm || 4000) + 'mm.');
            if (widthErr) widthErr.textContent = 'Maximum width is ' + (this._data.maxWidthMm || 4000) + 'mm';
            var wEl2 = document.getElementById('gc8-width');
            if (wEl2) wEl2.classList.add('pr-input--error');
            return false;
          }
          if (state.height > (this._data.maxHeightMm || 4000)) {
            announce('Height exceeds maximum of ' + (this._data.maxHeightMm || 4000) + 'mm.');
            if (heightErr) heightErr.textContent = 'Maximum height is ' + (this._data.maxHeightMm || 4000) + 'mm';
            var hEl2 = document.getElementById('gc8-height');
            if (hEl2) hEl2.classList.add('pr-input--error');
            return false;
          }
          return true;

        default:
          return true;
      }
    },

    /** Business rules: auto-toughened for doors/skylights, gate design options. */
    applyBusinessRules: function () {
      var state = State.getState();
      var installType = state.installType;

      var isConservatory = installType === 'conservatory';
      var isRoof = installType === 'skylight' || (isConservatory && state.conservatoryType === 'roof');
      var isDoor = installType === 'door' || (isConservatory && state.conservatoryType === 'door');
      var needsToughened = isRoof || isDoor;

      // Safety notice banner
      var safetyNotice = document.getElementById('gc8-safety-notice');
      var safetyReason = document.getElementById('gc8-safety-reason');
      if (safetyNotice) {
        safetyNotice.style.display = needsToughened ? 'flex' : 'none';
        if (safetyReason) {
          if (isDoor) {
            safetyReason.textContent = 'Building Regulations require toughened or laminated safety glass for doors. This will be automatically applied.';
          } else if (isRoof) {
            safetyReason.textContent = 'Building Regulations require toughened glass for overhead glazing and skylights. This will be automatically applied.';
          }
        }
      }

      // Auto-enable toughened for safety-critical installs
      var toughenedEl = document.querySelector('[data-gc8-field="toughened"]');
      if (toughenedEl) {
        if (needsToughened) {
          toughenedEl.checked = true;
          toughenedEl.disabled = true;
          State.setState('toughened', true);
        } else {
          toughenedEl.disabled = false;
        }
      }

      // Auto-toughened label
      var autoLabel = document.querySelector('.gc8__toughened-auto');
      if (autoLabel) {
        autoLabel.style.display = needsToughened ? 'inline' : 'none';
      }

      // Hide catflap for skylights/conservatory-roof
      var catflapRow = document.querySelector('[data-gc8-field="catflap"]');
      if (catflapRow) {
        var catflapGroup = catflapRow.closest('.gc8__design-group') || catflapRow.closest('.gc8__toggle-row');
        if (catflapGroup) catflapGroup.style.display = isRoof ? 'none' : 'block';
        if (isRoof) {
          catflapRow.checked = false;
          State.setState('catflap', false);
          var catflapConfig = document.getElementById('gc8-catflap-config');
          if (catflapConfig) catflapConfig.style.display = 'none';
        }
      }

      // Letterbox only for doors
      var letterboxRow = document.getElementById('gc8-letterbox-row');
      if (letterboxRow) {
        letterboxRow.style.display = isDoor ? 'flex' : 'none';
        if (!isDoor) {
          var letterboxEl = document.querySelector('[data-gc8-field="letterbox"]');
          if (letterboxEl) { letterboxEl.checked = false; State.setState('letterbox', false); }
        }
      }

      // Georgian bars conflict with privacy glass (only available with clear glass)
      var georgianConflict = document.getElementById('gc8-georgian-conflict');
      var hasPrivacy = state.privacyGlass && state.privacyGlass !== '' && state.privacyGlass !== 'none';
      if (georgianConflict) {
        georgianConflict.style.display = (state.georgianEnabled && hasPrivacy) ? 'flex' : 'none';
      }

      // Oversize warning
      var isOversize = exceedsDeliveryMax(state.width, state.height);
      var warnEl = document.getElementById('gc8-oversize-warning');
      if (warnEl) warnEl.style.display = isOversize ? 'flex' : 'none';
    },

    /** Populate the review table (step 5). */
    populateReview: function () {
      var body = document.getElementById('gc8-review-table');
      if (!body) return;

      var state = State.getState();
      var html = '';

      var installLabel = formatValue(state.installType);
      if (state.installType === 'conservatory' && state.conservatoryType) {
        var subtypeLabels = { roof: 'Roof Panel', side: 'Side Window', door: 'Door Panel' };
        installLabel = 'Conservatory \u2014 ' + (subtypeLabels[state.conservatoryType] || formatValue(state.conservatoryType));
      }
      html += '<tr><td>Installation</td><td>' + installLabel + '</td></tr>';
      if (state.frameMaterial) {
        html += '<tr><td>Frame Material</td><td>' + formatValue(state.frameMaterial) + '</td></tr>';
      }
      html += '<tr><td>Dimensions</td><td>' + state.width + ' \u00D7 ' + state.height + ' mm</td></tr>';
      html += '<tr><td>Glass (Inner Pane)</td><td>' + (formatValue(state.glassType) || 'Standard') + '</td></tr>';
      html += '<tr><td>Glass (Outer Pane)</td><td>' + formatValue(state.outerPane) + '</td></tr>';
      if (state.construction === 'triple') html += '<tr><td>Glazing</td><td>Triple</td></tr>';

      if (state.performanceTier) html += '<tr><td>Performance</td><td>' + formatValue(state.performanceTier) + '</td></tr>';
      if (state.privacyGlass && state.privacyGlass !== 'none') {
        html += '<tr><td>Privacy Glass</td><td>' + formatValue(state.privacyGlass) + '</td></tr>';
      }
      if (state.spacerColour) html += '<tr><td>Spacer</td><td>' + (state.spacerType === 'super' ? 'Super Spacer — ' : '') + formatValue(state.spacerColour) + '</td></tr>';
      if (state.cavityWidth) html += '<tr><td>Spacer Size</td><td>' + state.cavityWidth + ' mm</td></tr>';
      if (state.laminated && state.laminated !== 'none') {
        html += '<tr><td>Laminated Glass</td><td>' + formatLaminatedLabel(state.laminated) + '</td></tr>';
      }
      if (state.toughened) html += '<tr><td>Toughened Glass</td><td>Yes</td></tr>';
      if (state.solar) html += '<tr><td>Solar Control</td><td>Yes</td></tr>';
      if (state.argon) html += '<tr><td>Argon Gas Fill</td><td>Yes</td></tr>';
      if (state.selfclean) html += '<tr><td>Self-Cleaning</td><td>Yes</td></tr>';
      if (state.georgianEnabled) {
        html += '<tr><td>Georgian Bars</td><td>' + formatValue(state.georgianColour) + ', ' + state.georgianWidth + 'mm, ' + state.georgianAcross + ' across \u00D7 ' + state.georgianDown + ' down</td></tr>';
        html += '<tr><td>Bar Spacing</td><td>' + (state.georgianSpacingMode === 'custom' ? 'Custom (\u00A2 centre-to-centre)' : 'Equal') + '</td></tr>';
        if (state.georgianSpacingMode === 'custom') {
          var vp = state.georgianVPositions || [];
          var hp = state.georgianHPositions || [];
          for (var vri = 0; vri < vp.length; vri++) {
            html += '<tr><td>\u00A2 Vertical Bar ' + (vri + 1) + '</td><td>' + vp[vri] + 'mm from left</td></tr>';
          }
          for (var hri = 0; hri < hp.length; hri++) {
            html += '<tr><td>\u00A2 Horizontal Bar ' + (hri + 1) + '</td><td>' + hp[hri] + 'mm from top</td></tr>';
          }
        }
      }
      if (state.leadEnabled) {
        html += '<tr><td>Decorative Lead</td><td>' + formatValue(state.leadPattern) + ' pattern</td></tr>';
      }
      if (state.catflap) {
        var sizeLabels = { small: 'Small (165\u00D7171mm)', medium: 'Medium (221\u00D7227mm)', large: 'Large (280\u00D7300mm)' };
        html += '<tr><td>Catflap Cutout</td><td>' + (sizeLabels[state.catflapSize] || 'Medium') + '</td></tr>';
      }
      if (state.letterbox) html += '<tr><td>Letterbox Cutout</td><td>Yes</td></tr>';
      if (state.specialRequests) {
        html += '<tr><td>Notes</td><td>' + state.specialRequests.substring(0, 80) + '</td></tr>';
      }

      var price = PricingEngine._lastPrice;
      html += '<tr class="gc8__review-total"><td><strong>Unit Price</strong></td><td><strong>' + formatPrice(price) + '</strong></td></tr>';
      if (state.quantity > 1) {
        html += '<tr class="gc8__review-total"><td><strong>Total (' + state.quantity + '\u00D7)</strong></td><td><strong>' + formatPrice(price * state.quantity) + '</strong></td></tr>';
      }

      body.innerHTML = html;
    },

    /** Populate dynamic technical specifications (step 5). */
    populateTechSpecs: function () {
      var grid = document.getElementById('gc8-techspecs-grid');
      if (!grid) return;

      var state = State.getState();
      var gt = (state.glassType || '').toLowerCase();
      var isTriple = state.construction === 'triple' || gt.indexOf('triple') !== -1;
      var hasLowE = gt.indexOf('low-e') !== -1;

      // Estimate U-value (mirrors canvas estimateUValue)
      var u = 5.8;
      if (isTriple) u = 0.8;
      else if (hasLowE) u = 1.4;
      else if (gt.indexOf('double') !== -1 || gt.indexOf('float') === -1) u = 2.8;
      if (state.argon) u -= 0.3;
      if (state.spacerColour === 'black') u -= 0.1;
      if (state.solar) u -= 0.1;
      u = Math.max(0.6, Math.round(u * 10) / 10);

      // Energy rating letter
      var rating = 'E';
      if (u <= 1.0) rating = 'A+';
      else if (u <= 1.2) rating = 'A';
      else if (u <= 1.4) rating = 'B';
      else if (u <= 1.8) rating = 'C';
      else if (u <= 2.5) rating = 'D';

      // Build specs array
      var specs = [];
      specs.push({ label: 'U-Value', value: u.toFixed(1) + ' W/m\u00B2K', highlight: u <= 1.4 ? 'good' : u <= 2.0 ? 'ok' : 'poor' });
      specs.push({ label: 'Energy Rating', value: rating, highlight: rating === 'A+' || rating === 'A' ? 'good' : rating === 'B' ? 'ok' : 'poor' });
      specs.push({ label: 'Unit Type', value: isTriple ? 'Triple Glazed' : 'Double Glazed' });
      specs.push({ label: 'Total Thickness', value: (parseInt(state.cavityWidth || '20', 10) + 8) + 'mm' });

      if (hasLowE) specs.push({ label: 'Coating', value: 'Low-E (Pilkington K-Glass)' });
      specs.push({ label: 'Gas Fill', value: state.argon ? 'Argon (90%+)' : 'Dry Air' });

      // Spacer bar
      var spacerTypeLabel = state.spacerType === 'super' ? 'Super Spacer' : 'Aluminium Spacer';
      var spacerCol = formatValue(state.spacerColour || 'silver');
      specs.push({ label: 'Spacer Bar', value: spacerTypeLabel + ' \u2014 ' + spacerCol });

      if (state.cavityWidth) specs.push({ label: 'Spacer Size', value: state.cavityWidth + 'mm' });

      specs.push({ label: 'Outer Pane', value: formatValue(state.outerPane) });

      // Safety
      if (state.toughened) specs.push({ label: 'Safety Glass', value: 'Toughened to BS EN 12150' });
      if (state.laminated && state.laminated !== 'none') {
        specs.push({ label: 'Laminated', value: formatLaminatedLabel(state.laminated) });
      }

      // Extras
      if (state.solar) specs.push({ label: 'Solar Control', value: 'Yes \u2014 reduces glare & heat gain' });
      if (state.selfclean) specs.push({ label: 'Self-Cleaning', value: 'Pilkington Activ\u2122' });

      var priv = state.privacyGlass || '';
      if (priv && priv !== 'none') specs.push({ label: 'Privacy', value: formatValue(priv) });

      // Edge finish
      specs.push({ label: 'Edge Finish', value: 'Arrised (smooth polished)' });

      // Compliance
      var compliant = u <= 1.4;
      specs.push({ label: 'Building Regs', value: compliant ? 'Part L 2021 Compliant' : 'Check with Building Control', highlight: compliant ? 'good' : 'poor' });

      // Fixed business info
      specs.push({ label: 'Warranty', value: '10 years (sealed unit)' });
      specs.push({ label: 'Lead Time', value: leadTimeText(state) });

      // Render
      var html = '';
      for (var i = 0; i < specs.length; i++) {
        var s = specs[i];
        var cls = 'gc8__ts-row';
        var valCls = 'gc8__ts-val';
        if (s.highlight === 'good') valCls += ' gc8__ts-val--good';
        else if (s.highlight === 'poor') valCls += ' gc8__ts-val--poor';
        else if (s.highlight === 'ok') valCls += ' gc8__ts-val--ok';
        html += '<div class="' + cls + '"><span class="gc8__ts-label">' + s.label + '</span><span class="' + valCls + '">' + s.value + '</span></div>';
      }
      grid.innerHTML = html;
    }
  };


  /* ═══════════════════════════════════════════════════════════════════════════
   * 4. BOTTOM SHEET CONTROLLER (mobile only)
   * ═══════════════════════════════════════════════════════════════════════════ */

  var BottomSheetController = {
    _sheet: null,
    _handle: null,
    _startY: 0,
    _startTranslate: 0,
    _currentSnap: 'collapsed',
    _mql: null,
    _enabled: false,

    SNAP_COLLAPSED: 80,
    SNAP_HALF_VH: 55,
    SNAP_FULL_VH: 90,

    init: function () {
      this._sheet = document.querySelector('.gc8__sheet');
      this._handle = document.querySelector('.gc8__sheet-handle');
      if (!this._sheet || !this._handle) return;

      var self = this;
      this._mql = window.matchMedia('(max-width: 1023px)');

      function onMediaChange(e) {
        if (e.matches) {
          self.enable();
        } else {
          self.disable();
        }
      }

      // Initial check
      if (this._mql.matches) {
        this.enable();
      }

      // Watch for changes
      if (this._mql.addEventListener) {
        this._mql.addEventListener('change', onMediaChange);
      } else if (this._mql.addListener) {
        this._mql.addListener(onMediaChange);
      }
    },

    enable: function () {
      if (this._enabled) return;
      this._enabled = true;

      var self = this;
      this._sheet.classList.add('gc8__sheet--collapsed');
      this._currentSnap = 'collapsed';

      this._handle.addEventListener('touchstart', function (e) {
        self._onTouchStart(e);
      }, { passive: true });

      this._handle.addEventListener('touchmove', function (e) {
        self._onTouchMove(e);
      }, { passive: true });

      this._handle.addEventListener('touchend', function (e) {
        self._onTouchEnd(e);
      }, { passive: true });

      // Handle click toggles between collapsed and half
      this._handle.addEventListener('click', function () {
        if (self._currentSnap === 'collapsed') {
          self.snapTo('half');
        } else {
          self.snapTo('collapsed');
        }
      });

      // Wire sheet nav buttons
      var sheetBack = document.getElementById('gc8-sheet-back');
      var sheetNext = document.getElementById('gc8-sheet-next');

      if (sheetBack) {
        sheetBack.addEventListener('click', function () {
          var step = WizardController.getCurrentStep();
          if (step > 1) WizardController.goToStep(step - 1);
        });
      }

      if (sheetNext) {
        sheetNext.addEventListener('click', function () {
          var step = WizardController.getCurrentStep();
          if (step < WizardController._totalSteps) WizardController.goToStep(step + 1);
        });
      }

      // Sync sheet price on price updates
      document.addEventListener('gc8:price-update', function (e) {
        var sheetPrice = document.getElementById('gc8-sheet-price');
        if (sheetPrice && e.detail) {
          sheetPrice.textContent = formatPrice(e.detail.unitPrice || 0);
        }
      });
    },

    disable: function () {
      this._enabled = false;
      if (this._sheet) {
        this._sheet.classList.remove('gc8__sheet--collapsed', 'gc8__sheet--half', 'gc8__sheet--full');
        this._sheet.style.transform = '';
      }
    },

    _getSnapPx: function (name) {
      var vh = window.innerHeight;
      switch (name) {
        case 'collapsed': return vh - this.SNAP_COLLAPSED;
        case 'half': return vh - (vh * this.SNAP_HALF_VH / 100);
        case 'full': return vh - (vh * this.SNAP_FULL_VH / 100);
        default: return vh - this.SNAP_COLLAPSED;
      }
    },

    _onTouchStart: function (e) {
      var touch = e.changedTouches[0];
      this._startY = touch.clientY;
      this._startTranslate = this._getSnapPx(this._currentSnap);
      this._sheet.style.transition = 'none';
    },

    _onTouchMove: function (e) {
      var touch = e.changedTouches[0];
      var deltaY = touch.clientY - this._startY;
      var newTranslate = Math.max(this._getSnapPx('full'), this._startTranslate + deltaY);
      this._sheet.style.transform = 'translateY(' + newTranslate + 'px)';
    },

    _onTouchEnd: function (e) {
      var touch = e.changedTouches[0];
      var deltaY = touch.clientY - this._startY;
      var velocity = deltaY; // Simplified velocity (positive = dragging down)

      this._sheet.style.transition = '';
      this._sheet.style.transform = '';

      // Velocity-based snapping
      if (Math.abs(velocity) > 80) {
        // Fast flick
        if (velocity > 0) {
          // Dragging down — snap to next lower point
          if (this._currentSnap === 'full') this.snapTo('half');
          else this.snapTo('collapsed');
        } else {
          // Dragging up — snap to next higher point
          if (this._currentSnap === 'collapsed') this.snapTo('half');
          else this.snapTo('full');
        }
      } else {
        // Slow drag — snap to nearest
        var currentPx = this._startTranslate + deltaY;
        var collapsedPx = this._getSnapPx('collapsed');
        var halfPx = this._getSnapPx('half');
        var fullPx = this._getSnapPx('full');

        var dCollapsed = Math.abs(currentPx - collapsedPx);
        var dHalf = Math.abs(currentPx - halfPx);
        var dFull = Math.abs(currentPx - fullPx);

        if (dCollapsed <= dHalf && dCollapsed <= dFull) this.snapTo('collapsed');
        else if (dHalf <= dFull) this.snapTo('half');
        else this.snapTo('full');
      }
    },

    snapTo: function (point) {
      if (!this._sheet) return;
      this._currentSnap = point;

      this._sheet.classList.remove('gc8__sheet--collapsed', 'gc8__sheet--half', 'gc8__sheet--full');
      this._sheet.classList.add('gc8__sheet--' + point);

      // Lock body scroll when full
      if (point === 'full') {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  };


  /* ═══════════════════════════════════════════════════════════════════════════
   * 5. CHECKOUT CONTROLLER
   * ═══════════════════════════════════════════════════════════════════════════ */

  var CheckoutController = {
    _data: null,
    _submitting: false,

    init: function (data) {
      this._data = data;
      this.bindEvents();
    },

    bindEvents: function () {
      var self = this;

      // Buy Now button (W4.7 2026-05-14: rebound from addToCart() to
      // buyNow() — draft-order-invoice-redirect path replaces the
      // legacy /cart/add.js path. Real price set server-side; no
      // drift possible.)
      var cartBtn = document.getElementById('gc8-cart-btn');
      if (cartBtn) {
        cartBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.buyNow();
        });
      }

      // Quote button opens modal
      var quoteBtn = document.getElementById('gc8-quote-btn');
      if (quoteBtn) {
        quoteBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.openQuoteModal();
        });
      }

      // Quote form submission
      var quoteForm = document.getElementById('gc8-quote-form');
      if (quoteForm) {
        quoteForm.addEventListener('submit', function (e) {
          e.preventDefault();
          self.submitQuote();
        });
      }

      // Quote modal close (click overlay, close btn, escape)
      var modal = document.getElementById('gc8-quote-modal');
      if (modal) {
        modal.addEventListener('click', function (e) {
          if (e.target === modal) self.closeQuoteModal();
        });

        var closeBtn = modal.querySelector('.gc8__modal-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', function () { self.closeQuoteModal(); });
        }
      }

      // Quote done button (success state)
      var doneBtn = document.getElementById('gc8-quote-done');
      if (doneBtn) {
        doneBtn.addEventListener('click', function () { self.closeQuoteModal(); });
      }

      // "Prefer a quote?" link opens modal
      var preferBtn = document.getElementById('gc8-prefer-quote');
      if (preferBtn) {
        preferBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.openQuoteModal();
        });
      }

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.closeQuoteModal();
      });

      // Listen for price updates to toggle cart/quote buttons
      document.addEventListener('gc8:price-update', function () {
        self.updateCheckoutMode();
      });
    },

    /** Route to cart or quote based on oversize/complexity. Only operates on Review step. */
    updateCheckoutMode: function () {
      // Only toggle cart/quote visibility on the final step
      if (WizardController.getCurrentStep() !== WizardController._totalSteps) return;

      var state = State.getState();
      // Count design extras for complexity check
      var designCount = 0;
      if (state.georgianEnabled) designCount++;
      if (state.leadEnabled) designCount++;
      if (state.catflap) designCount++;
      if (state.letterbox) designCount++;
      var isComplex = designCount >= 3;

      // W4.7 2026-05-14 (lifted from hotfix): cart button is now the
      // safe drift-free buyNow() path (Railway draft-order →
      // invoice_url redirect). Restore the original oversize/complex
      // → quote-only routing — oversize / heavy-design configs still
      // benefit from sales-team review via the email-invoice path,
      // even though the cart path is now safe. Standard configurations
      // hit Buy Now → instant checkout.
      // Units larger than we deliver (2900 × 1500mm) are quote-only too.
      var needsQuote = isComplex || exceedsDeliveryMax(state.width, state.height);

      var cartBtn = document.getElementById('gc8-cart-btn');
      var quoteBtn = document.getElementById('gc8-quote-btn');

      if (cartBtn && quoteBtn) {
        if (needsQuote) {
          cartBtn.style.display = 'none';
          quoteBtn.style.display = 'inline-flex';
        } else {
          cartBtn.style.display = 'inline-flex';
          quoteBtn.style.display = 'none';
        }
      }
    },

    /** Build line item properties object from current state.
     *
     *  Customer-facing vs ops-only split:
     *  Keys without an underscore prefix render on the buyer's checkout
     *  + order-confirmation views. Keys with a leading underscore are
     *  Shopify's hidden-property convention — visible only in Admin and
     *  webhook payloads. Ops data MUST be underscore-prefixed; this is
     *  the rule that prevents a repeat of the cart-vs-checkout drift
     *  hotfixes (ccb861a, 3adc82d). */
    buildProperties: function () {
      var state = State.getState();
      var price = PricingEngine._lastPrice;

      var props = {
        'Installation': formatValue(state.installType),
        'Width (mm)': String(state.width),
        'Height (mm)': String(state.height),
        'Glass Type': formatValue(state.glassType) || 'Standard Double Glazed',
        // Ops-only: numeric price for reconciliation, formatted version for
        // Admin readability, configurator-build version for issue triage.
        // None of these surface to the buyer.
        '_calculated_price': price.toFixed(2),
        '_calculated_price_display': formatPrice(price),
        '_configurator_version': 'v8'
      };

      // Frame Material: only meaningful where a frame is actually being
      // supplied. Glass-only orders (window, skylight) don't carry frame
      // hardware — showing this row on a glass-only checkout is noise.
      // Door + conservatory orders specify a frame and the material drives
      // ironmongery, threshold, and bead profile.
      var frameRelevantInstalls = ['door', 'conservatory'];
      if (state.frameMaterial && frameRelevantInstalls.indexOf(state.installType) !== -1) {
        props['Frame Material'] = formatValue(state.frameMaterial);
      }
      if (state.performanceTier && state.performanceTier !== 'Standard') props['Performance'] = formatValue(state.performanceTier);
      if (state.privacyGlass && state.privacyGlass !== 'none' && state.privacyGlass !== 'None') props['Privacy Glass'] = formatValue(state.privacyGlass);
      props['Outer Pane'] = formatValue(state.outerPane);
      if (state.construction === 'triple') props['Glazing'] = 'Triple';
      if (state.spacerColour) props['Spacer Colour'] = (state.spacerType === 'super' ? 'Super Spacer ' : '') + formatValue(state.spacerColour);
      if (state.cavityWidth) props['Spacer Size'] = state.cavityWidth + ' mm';
      if (state.laminated && state.laminated !== 'none') props['Laminated Glass'] = formatLaminatedLabel(state.laminated);
      if (state.toughened) props['Toughened Glass'] = 'Yes';
      if (state.solar) props['Solar Control'] = 'Yes';
      if (state.argon) props['Argon Gas Fill'] = 'Yes';
      if (state.selfclean) props['Self-Cleaning'] = 'Yes';
      if (state.installType === 'conservatory' && state.conservatoryType) {
        var ctLabels = { roof: 'Roof Panel', side: 'Side Window', door: 'Door Panel' };
        props['Conservatory Type'] = ctLabels[state.conservatoryType] || formatValue(state.conservatoryType);
      }
      if (state.georgianEnabled) {
        props['Georgian Bars'] = formatValue(state.georgianColour) + ', ' + state.georgianWidth + 'mm, ' + state.georgianAcross + ' across \u00D7 ' + state.georgianDown + ' down';
        props['Bar Spacing'] = state.georgianSpacingMode === 'custom' ? 'Custom (centre-to-centre)' : 'Equal';
        if (state.georgianSpacingMode === 'custom') {
          var vPos = state.georgianVPositions || [];
          var hPos = state.georgianHPositions || [];
          for (var vi = 0; vi < vPos.length; vi++) {
            props['Vertical Bar ' + (vi + 1)] = vPos[vi] + 'mm from left (\u00A2)';
          }
          for (var hi = 0; hi < hPos.length; hi++) {
            props['Horizontal Bar ' + (hi + 1)] = hPos[hi] + 'mm from top (\u00A2)';
          }
        }
      }
      if (state.leadEnabled) props['Decorative Lead'] = formatValue(state.leadPattern) + ' pattern';
      if (state.catflap) props['Catflap Cutout'] = state.catflapSize ? formatValue(state.catflapSize) : 'Medium';
      if (state.letterbox) props['Letterbox Cutout'] = 'Yes';
      if (state.specialRequests) props['Special Requests'] = state.specialRequests.substring(0, 250);

      return props;
    },

    /** Buy Now — drives the cart button. Routes through Railway draft-
     *  order endpoint to get a Shopify invoice_url, then redirects the
     *  buyer to that hosted checkout. Real line-item price set server-
     *  side by Railway; no cart-display vs checkout-charge drift
     *  possible. Works for ANY configuration (standard + custom dims).
     *
     *  Effectively W4.7 implemented via draft-order-invoice-redirect
     *  pattern, replacing the legacy /cart/add.js path entirely. Does
     *  not depend on per-dimension variants existing (those are still
     *  useful for Google Shopping feed via W4, but not required for
     *  this safe-checkout path). */
    buyNow: function () {
      if (this._submitting) return;

      var result = PricingEngine.calculate(State.getState(), this._data);

      // Larger than the biggest unit we deliver (Facts Sheet v1): no instant
      // checkout. Send the buyer to the quote flow with the reason shown.
      if (result.exceedsDeliveryMax) {
        this.openQuoteModal('over-max-size');
        return;
      }

      if (!result.variant) {
        this.showError('Please complete all configuration steps first.');
        return;
      }

      var self = this;

      // Postcode step: Buy Now delivers anywhere on the UK mainland (agreed
      // 2026-10-08; the earlier ~2hr-of-Bristol zone is lifted). Offshore /
      // non-mainland buyers go to the quote modal for a hand-quoted delivery.
      // The postcode also sets the minimum order (lower in Bristol), so the
      // price is recalculated once it is known.
      this.promptPostcodeAndRoute(function (parsedPostcode, email) {
        var finalResult = PricingEngine.calculate(State.getState(), self._data, {
          local: isLocalPostcode(parsedPostcode)
        });
        self.executeBuyNow(finalResult, parsedPostcode, email);
      });
    },

    /** Modal asking buyer for a UK postcode before committing to checkout.
     *  Calls `onMainland(parsedPostcode)` when the buyer is on the UK
     *  mainland; hands off to the quote modal (flagged non-mainland) for
     *  offshore postcodes. Buyer can cancel — no state change.
     *
     *  Restores focus to the element that triggered the modal on close so
     *  keyboard users don't lose their place in the page flow. */
    promptPostcodeAndRoute: function (onMainland) {
      var self = this;
      var triggerEl = document.activeElement;
      var overlay = document.getElementById('gc8-postcode-overlay');
      if (overlay) overlay.parentNode.removeChild(overlay);

      overlay = document.createElement('div');
      overlay.id = 'gc8-postcode-overlay';
      overlay.className = 'gc8__postcode-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-labelledby', 'gc8-postcode-title');

      overlay.innerHTML =
        '<div class="gc8__postcode-modal">' +
          '<button type="button" class="gc8__postcode-close" aria-label="Close">×</button>' +
          '<h3 id="gc8-postcode-title">Almost there</h3>' +
          '<p class="gc8__postcode-help">Enter your email and UK postcode to continue to checkout. ' +
            'Prices include VAT. Delivery is added at checkout.</p>' +
          '<div class="gc8__postcode-field">' +
            '<label for="gc8-email-input" class="visually-hidden">Email address</label>' +
            '<input id="gc8-email-input" type="email" autocomplete="email" ' +
              'inputmode="email" spellcheck="false" placeholder="Email address" required>' +
          '</div>' +
          '<div class="gc8__postcode-field">' +
            '<label for="gc8-postcode-input" class="visually-hidden">UK postcode</label>' +
            '<input id="gc8-postcode-input" type="text" autocomplete="postal-code" ' +
              'inputmode="text" spellcheck="false" placeholder="UK postcode (e.g. GL2 4JD)" maxlength="10" required>' +
          '</div>' +
          '<p class="gc8__postcode-help gc8__postcode-consent">We\'ll email your order details and ' +
            'can help if you don\'t finish checkout.</p>' +
          '<p class="gc8__postcode-error" id="gc8-postcode-error" role="alert" hidden></p>' +
          '<div class="gc8__postcode-actions">' +
            '<button type="button" class="gc8__postcode-cancel">Cancel</button>' +
            '<button type="button" class="gc8__postcode-confirm">Continue to checkout</button>' +
          '</div>' +
        '</div>';

      document.body.appendChild(overlay);
      document.body.classList.add('gc8__postcode-open');

      var emailInput = overlay.querySelector('#gc8-email-input');
      var input = overlay.querySelector('#gc8-postcode-input');
      var errorEl = overlay.querySelector('#gc8-postcode-error');
      var confirmBtn = overlay.querySelector('.gc8__postcode-confirm');
      var cancelBtn = overlay.querySelector('.gc8__postcode-cancel');
      var closeBtn = overlay.querySelector('.gc8__postcode-close');

      function close() {
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        document.body.classList.remove('gc8__postcode-open');
        if (triggerEl && typeof triggerEl.focus === 'function') {
          try { triggerEl.focus(); } catch (e) { /* element gone */ }
        }
      }

      function showError(msg) {
        errorEl.textContent = msg;
        errorEl.hidden = false;
      }

      function submit() {
        // Capture the buyer's email BEFORE the checkout redirect so an
        // abandoned Buy Now becomes a recoverable lead (draft carries the
        // email) instead of an anonymous "Customer: None" draft.
        var email = (emailInput.value || '').trim();
        if (!isValidEmail(email)) {
          showError('Enter a valid email address.');
          emailInput.focus();
          return;
        }
        var parsed = parseUkPostcode(input.value);
        if (!parsed) {
          showError('Enter a valid UK postcode.');
          input.focus();
          return;
        }
        if (!isUkMainlandPostcode(parsed)) {
          // Non-mainland: close postcode modal, open quote modal with a
          // flag so the quote request is tagged for ops follow-up. Carry the
          // email so the quote modal can prefill it (no double-asking).
          close();
          self._pendingBuyNowEmail = email;
          self.openNonMainlandQuoteFlow(parsed);
          return;
        }
        close();
        onMainland(parsed, email);
      }

      confirmBtn.addEventListener('click', submit);
      cancelBtn.addEventListener('click', close);
      closeBtn.addEventListener('click', close);
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) close();
      });
      function onEnter(e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          submit();
        }
      }
      emailInput.addEventListener('keydown', onEnter);
      input.addEventListener('keydown', onEnter);

      // Defer focus so the modal is rendered before the focus attempt. Focus
      // the email field first — it's the first field in the flow now.
      setTimeout(function () { emailInput.focus(); }, 50);
    },

    /** Open the existing quote modal with non-mainland routing context.
     *  Reuses the same form ops already see in Admin; the tag flag lets them
     *  filter for non-mainland orders + over time build proper rate cards
     *  for high-demand zones. */
    openNonMainlandQuoteFlow: function (parsedPostcode) {
      this._pendingNonMainland = {
        postcode: parsedPostcode.formatted,
        area: parsedPostcode.area
      };
      if (typeof this.openQuoteModal === 'function') {
        this.openQuoteModal('non-mainland');
      } else {
        // Defensive: if quote modal helper is missing in some embed, show
        // an inline message asking the buyer to contact ops directly.
        this.showError(
          'Postcode ' + parsedPostcode.formatted + ' is outside UK mainland. ' +
          'Email orders@panerelief.co.uk for a shipping quote — we usually ' +
          'reply within 24 hours.'
        );
      }
    },

    /** Open the quote modal for a mainland-but-out-of-delivery-area buyer
     *  (beyond ~2hr of Bristol). Same form + lead capture as non-mainland, but
     *  tagged `out-of-area` so ops can triage separately from offshore quotes.
     *  GitHub #2. */
    openOutOfAreaQuoteFlow: function (parsedPostcode) {
      this._pendingOutOfArea = {
        postcode: parsedPostcode.formatted,
        area: parsedPostcode.area
      };
      if (typeof this.openQuoteModal === 'function') {
        this.openQuoteModal('out-of-area');
      } else {
        this.showError(
          'Postcode ' + parsedPostcode.formatted + ' is outside our delivery area. ' +
          'Email orders@panerelief.co.uk for a quote — we usually reply within ' +
          '24 hours.'
        );
      }
    },

    /** Execute the actual draft-order-invoice POST once the buyer is
     *  confirmed mainland. Extracted out of buyNow() so the postcode gate
     *  reads cleanly. */
    executeBuyNow: function (result, parsedPostcode, buyerEmail) {
      // Buy Now is always an in-zone mainland flow. Clear any stale routing
      // flags left behind by a previous quote-modal interaction so we don't
      // carry ops state across user sessions on the same page.
      this._pendingNonMainland = null;
      this._pendingOutOfArea = null;

      var self = this;
      var state = State.getState();
      // result is re-priced for the buyer's postcode (local minimum), so it is
      // the source of truth, not the last animated display price.
      var price = parseFloat((result.unitPrice || PricingEngine._lastPrice || 0).toFixed(2));
      var props = this.buildProperties();

      this._submitting = true;
      this.setButtonState('gc8-cart-btn', 'loading', 'Preparing checkout…');

      var payload = {
        variant_id: result.variant.id,
        quote_type: 'product',
        quantity: state.quantity || 1,
        // Buyer email captured in the postcode modal before redirect. Railway
        // records it on the draft so an abandoned Buy Now is a recoverable lead
        // (not an anonymous "Customer: None" draft). Empty object if absent.
        customer: buyerEmail ? { email: buyerEmail } : {},
        config: {},
        tags: ['configurator-buy-now', 'v8-configurator',
               ('product-' + (this._data.product.handle || 'unknown')).substring(0, 40)],
        calculated_price: price,
        display_title: buildDisplayTitle(this._data.product.title, state)
      };

      // GA4 attribution for the server-side Measurement Protocol purchase that
      // Railway fires when this draft order is paid. Absent when consent denied.
      var ga = readGa4Attribution();
      if (ga.client_id) payload.ga_client_id = ga.client_id;
      if (ga.session_id) payload.ga_session_id = ga.session_id;

      // First-party order-source attribution (GA4 files these purchases as (not set)).
      attachAttribution(payload);

      // Configurator canvas snapshot for ops visibility. Saved server-side
      // as a hidden _configuration_preview line-item property — never shown
      // to the buyer. See the architectural rule at top of buildProperties.
      var snapshot = captureCanvasSnapshot();
      if (snapshot) payload.configuration_preview = snapshot;

      // Tag the postcode area so ops can spot regional patterns in the
      // Admin draft-order list. Keeps the buyer's actual postcode out of
      // the tag itself (tags are searchable/logged). Area is 1-2 chars,
      // total tag well under Shopify's 40-char limit.
      if (parsedPostcode && parsedPostcode.area) {
        payload.tags.push('postcode-area-' + parsedPostcode.area);
      }

      // Copy line-item properties into config; exclude underscore-prefixed
      // internal keys (matches submitQuote semantics). Railway will then
      // ALSO filter underscore keys server-side as defence-in-depth.
      for (var key in props) {
        if (props.hasOwnProperty(key) && key.charAt(0) !== '_') {
          payload.config[key] = props[key];
        }
      }
      payload.config['Product'] = unitLabel(this._data.product.title, state);
      // Area is ops-only — fulfilment-team use, not customer-facing.
      payload.config['_area_sqm'] = ((state.width * state.height) / 1000000).toFixed(2);
      if (parsedPostcode && parsedPostcode.formatted) {
        payload.config['_delivery_postcode'] = parsedPostcode.formatted;
      }

      fetchWithRetry(this._data.railwayApiUrl, payload)
        .then(function (data) {
          GTM.push('configurator_buy_now_submitted', {
            price: price,
            variant_id: result.variant.id,
            quantity: state.quantity || 1,
            postcode_area: (parsedPostcode && parsedPostcode.area) || null,
            email_captured: !!buyerEmail
          });
          if (data && data.invoice_url) {
            self.setButtonState('gc8-cart-btn', 'success', 'Redirecting…');
            // Redirect to Shopify-hosted checkout with the configured
            // draft order. Line-item price is the real configurator
            // price set by Railway server-side. No drift.
            window.location.href = data.invoice_url;
            return;
          }
          throw new Error('No invoice_url in Railway response');
        })
        .catch(function (err) {
          // Server delivery-zone gate rejected this as out-of-area (client
          // gate bypassed or a stale cached asset). Route to the quote flow
          // instead of a misleading "checkout unavailable" error. GitHub #2.
          if (err && err.status === 422 && err.body && err.body.out_of_area) {
            self.setButtonState('gc8-cart-btn', 'default',
              self._data.cartButtonText || 'Buy Now');
            self.openOutOfAreaQuoteFlow(parsedPostcode);
            return;
          }
          self.setButtonState('gc8-cart-btn', 'error', 'Try again');
          self.showError(
            'We couldn’t reach the checkout right now. ' +
            'Try again or request a quote and we’ll email an invoice.'
          );
          setTimeout(function () {
            self.setButtonState('gc8-cart-btn', 'default',
              self._data.cartButtonText || 'Buy Now');
          }, 3000);
        })
        .finally(function () { self._submitting = false; });
    },

    /** Legacy entry point: kept as a safe forwarding stub. The
     *  /cart/add.js path is gone; this re-routes any caller to the
     *  drift-free buyNow() path. */
    addToCart: function () {
      if (this._submitting) return;
      this.buyNow();
      return;
      // eslint-disable-next-line no-unreachable
      /* original cart-add code preserved below for the revert path */

      var result = PricingEngine.calculate(State.getState(), this._data);
      if (!result.variant) {
        this.showError('Please complete all configuration steps first.');
        return;
      }

      var self = this;
      var state = State.getState();
      this._submitting = true;
      this.setButtonState('gc8-cart-btn', 'loading', 'Adding...');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: result.variant.id,
            quantity: state.quantity || 1,
            properties: this.buildProperties()
          }]
        })
      })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        self.setButtonState('gc8-cart-btn', 'success', 'Added! Redirecting...');
        GTM.push('configurator_added_to_cart', {
          price: result.unitPrice,
          variant_id: result.variant.id,
          quantity: state.quantity
        });
        setTimeout(function () {
          window.location.href = '/cart';
        }, 800);
      })
      .catch(function (err) {
        self.setButtonState('gc8-cart-btn', 'error', 'Error \u2014 Try Again');
        self.showError('Failed to add to cart. Please try again.');
        setTimeout(function () {
          self.setButtonState('gc8-cart-btn', 'default', self._data.cartButtonText || 'Add to Cart');
        }, 3000);
      })
      .finally(function () {
        self._submitting = false;
      });
    },

    /** Open the quote request modal.
     *
     *  @param {string} [reason] — Optional context flag for the open. Accepts
     *     'non-mainland' (pre-fills from `_pendingNonMainland`) or 'out-of-area'
     *     (pre-fills from `_pendingOutOfArea`); either pre-fills the postcode
     *     field and surfaces a reason-specific banner explaining why the buyer
     *     was routed here instead of straight to checkout. */
    openQuoteModal: function (reason) {
      var modal = document.getElementById('gc8-quote-modal');
      if (!modal) return;

      // Reset to form view (hide success)
      var form = document.getElementById('gc8-quote-form');
      var success = document.getElementById('gc8-quote-success');
      if (form) form.style.display = 'flex';
      if (success) success.style.display = 'none';

      // Routing affordances: when Buy Now couldn't proceed (non-mainland OR
      // outside our delivery area), pre-fill the postcode + email and show a
      // banner so the buyer understands why they were routed to a quote
      // instead of instant checkout. One banner element, text by reason.
      var routeBanner = modal.querySelector('.gc8__quote-nonmainland-banner');
      var routeCtx = null;
      var bannerHtml = '';
      if (reason === 'non-mainland' && this._pendingNonMainland) {
        routeCtx = this._pendingNonMainland;
        bannerHtml =
          '<strong>Non-mainland delivery:</strong> ' +
          'Pricing shown is UK mainland only. Submit this quote request and we’ll ' +
          'reply with shipping to ' + routeCtx.postcode + ' within 24 hours.';
      } else if (reason === 'out-of-area' && this._pendingOutOfArea) {
        routeCtx = this._pendingOutOfArea;
        bannerHtml =
          '<strong>Outside our delivery area:</strong> ' +
          routeCtx.postcode + ' is beyond our usual delivery range, so we can’t ' +
          'offer instant checkout. Submit this quote request and we’ll confirm what ' +
          'we can do within 24 hours.';
      } else if (reason === 'over-max-size') {
        routeCtx = {};
        bannerHtml =
          '<strong>Larger than we can deliver:</strong> ' +
          'The largest unit we deliver is ' + MAX_DELIVERY_LONG_MM + 'mm long \u00D7 ' +
          MAX_DELIVERY_SHORT_MM + 'mm high, so this size can\u2019t go through instant ' +
          'checkout. Send us a quote request and we\u2019ll get back to you within 24 hours.';
      }
      if (routeCtx) {
        var pc = modal.querySelector('input[name="postcode"]');
        if (pc && routeCtx.postcode) pc.value = routeCtx.postcode;
        // Prefill the email captured in the postcode modal so routed buyers
        // aren't asked for it twice.
        var em = modal.querySelector('input[name="email"]');
        if (em && this._pendingBuyNowEmail) em.value = this._pendingBuyNowEmail;
        if (!routeBanner) {
          routeBanner = document.createElement('div');
          routeBanner.className = 'gc8__quote-nonmainland-banner';
          routeBanner.setAttribute('role', 'status');
          // The quote modal's content container is `.gc8__modal` (there is NO
          // `.gc8__modal-content`). Insert the banner just above the summary,
          // and ONLY into a real element node: inserting on modal.firstChild —
          // a whitespace text node — throws HierarchyRequestError, which aborts
          // the whole modal open and silently drops the buyer back to the
          // config (GitHub #2). Guard the node type so we never throw.
          var container = modal.querySelector('.gc8__modal');
          if (container && container.nodeType === 1) {
            var anchor = container.querySelector('.gc8__quote-summary') || container.firstChild;
            container.insertBefore(routeBanner, anchor);
          }
        }
        routeBanner.innerHTML = bannerHtml;
      } else if (routeBanner && routeBanner.parentNode) {
        routeBanner.parentNode.removeChild(routeBanner);
      }

      // Populate summary table
      this.populateQuoteSummary(modal);

      modal.style.display = 'flex';
      modal.classList.add('gc8__modal-overlay--visible');
      document.body.style.overflow = 'hidden';

      // Focus first input
      var fname = modal.querySelector('input[name="first_name"]');
      if (fname) setTimeout(function () { fname.focus(); }, 100);
    },

    closeQuoteModal: function () {
      var modal = document.getElementById('gc8-quote-modal');
      if (!modal) return;
      modal.classList.remove('gc8__modal-overlay--visible');
      setTimeout(function () { modal.style.display = 'none'; }, 200);
      document.body.style.overflow = '';
      // Drop any stale routing flags. Without this, a buyer who enters a
      // non-mainland or out-of-area postcode, lands here, cancels, and later
      // opens the quote modal via the generic "Request Quote" button would
      // have their next submission incorrectly tagged with the old routing.
      this._pendingNonMainland = null;
      this._pendingOutOfArea = null;
    },

    /** Populate the quote summary table with current config. */
    populateQuoteSummary: function (modal) {
      var body = document.getElementById('gc8-quote-summary-body');
      if (!body) return;
      var state = State.getState();
      var price = PricingEngine._lastPrice;
      var html = '';

      // Installation type
      var installLabel = formatValue(state.installType);
      if (state.installType === 'conservatory' && state.conservatoryType) {
        var subtypeLabels = { roof: 'Roof Panel', side: 'Side Window', door: 'Door Panel' };
        installLabel = 'Conservatory \u2014 ' + (subtypeLabels[state.conservatoryType] || formatValue(state.conservatoryType));
      }
      html += '<tr><td>Installation</td><td>' + installLabel + '</td></tr>';
      // Frame Material only relevant on installs that include a frame.
      // Glass-only orders (window, skylight) don't carry hardware tied to
      // the frame material.
      if (state.frameMaterial && (state.installType === 'door' || state.installType === 'conservatory')) {
        html += '<tr><td>Frame</td><td>' + formatValue(state.frameMaterial) + '</td></tr>';
      }
      html += '<tr><td>Dimensions</td><td>' + state.width + ' \u00D7 ' + state.height + ' mm</td></tr>';
      html += '<tr><td>Glass Type</td><td>' + (formatValue(state.glassType) || 'Standard') + '</td></tr>';
      if (state.performanceTier) html += '<tr><td>Performance</td><td>' + formatValue(state.performanceTier) + '</td></tr>';
      if (state.argon) html += '<tr><td>Argon Gas</td><td>Yes</td></tr>';
      if (state.toughened) html += '<tr><td>Toughened</td><td>Yes</td></tr>';
      if (state.solar) html += '<tr><td>Solar Control</td><td>Yes</td></tr>';
      if (state.privacyGlass && state.privacyGlass !== 'none') html += '<tr><td>Privacy</td><td>' + formatValue(state.privacyGlass) + '</td></tr>';
      if (state.laminated && state.laminated !== 'none') html += '<tr><td>Laminated</td><td>' + formatLaminatedLabel(state.laminated) + '</td></tr>';
      if (state.georgianEnabled) html += '<tr><td>Georgian Bars</td><td>Yes</td></tr>';
      if (state.leadEnabled) html += '<tr><td>Decorative Lead</td><td>Yes</td></tr>';
      if (state.catflap) html += '<tr><td>Catflap</td><td>Yes</td></tr>';
      if (state.letterbox) html += '<tr><td>Letterbox</td><td>Yes</td></tr>';

      // Price (always last, bold)
      html += '<tr><td><strong>Unit Price</strong></td><td><strong>' + formatPrice(price) + '</strong></td></tr>';
      if (state.quantity > 1) {
        html += '<tr><td><strong>Total (' + state.quantity + '\u00D7)</strong></td><td><strong>' + formatPrice(price * state.quantity) + '</strong></td></tr>';
      }
      body.innerHTML = html;
    },

    /** Submit quote request to Railway API. */
    submitQuote: function () {
      if (this._submitting) return;

      var modal = document.getElementById('gc8-quote-modal');
      if (!modal) return;

      var fname = (modal.querySelector('input[name="first_name"]') || {}).value;
      var lname = (modal.querySelector('input[name="last_name"]') || {}).value;
      var email = (modal.querySelector('input[name="email"]') || {}).value;
      var phone = (modal.querySelector('input[name="phone"]') || {}).value;
      var postcode = (modal.querySelector('input[name="postcode"]') || {}).value;
      var notes = (modal.querySelector('textarea[name="notes"]') || {}).value;

      fname = (fname || '').trim();
      lname = (lname || '').trim();
      email = (email || '').trim();
      phone = (phone || '').trim();
      postcode = (postcode || '').trim();
      notes = (notes || '').trim();

      if (!fname || !lname || !email) {
        this.showQuoteError('Please fill in all required fields.');
        return;
      }
      if (!isValidEmail(email)) {
        this.showQuoteError('Please enter a valid email address.');
        return;
      }

      var state = State.getState();
      var price = PricingEngine._lastPrice;
      var props = this.buildProperties();

      var payload = {
        quote_type: 'product',
        customer: {
          email: email,
          first_name: fname,
          last_name: lname
        },
        config: {},
        tags: ['configurator-quote', 'v8-configurator', ('product-' + (this._data.product.handle || 'unknown')).substring(0, 40)],
        calculated_price: price,
        display_title: buildDisplayTitle(this._data.product.title, state)
      };

      // GA4 attribution for the server-side Measurement Protocol purchase that
      // Railway fires when this draft order is paid. Absent when consent denied.
      var ga = readGa4Attribution();
      if (ga.client_id) payload.ga_client_id = ga.client_id;
      if (ga.session_id) payload.ga_session_id = ga.session_id;

      // First-party order-source attribution (GA4 files these purchases as (not set)).
      attachAttribution(payload);

      // Configurator canvas snapshot for ops visibility. Same ops-only
      // contract as buyNow(): server stores it as _configuration_preview,
      // never exposed to the customer.
      var quoteSnapshot = captureCanvasSnapshot();
      if (quoteSnapshot) payload.configuration_preview = quoteSnapshot;

      // Non-mainland routing: when this quote was opened from the postcode
      // gate, carry the flag through so ops can filter the resulting draft
      // order in Admin + over time build proper non-mainland rate cards.
      if (this._pendingNonMainland) {
        payload.tags.push('non-mainland-shipping');
        payload.tags.push(('shipping-zone-' + this._pendingNonMainland.area).substring(0, 40));
        payload.config['_delivery_zone'] = 'non-mainland';
        payload.config['_delivery_postcode'] = this._pendingNonMainland.postcode;
        this._pendingNonMainland = null;
      } else if (this._pendingOutOfArea) {
        // Out-of-delivery-area routing (mainland, beyond ~1hr of Bristol):
        // tag so ops can triage these separately from offshore quotes and
        // spot demand outside the current zone (GitHub #2).
        payload.tags.push('out-of-area');
        payload.tags.push(('out-of-area-' + this._pendingOutOfArea.area).substring(0, 40));
        payload.config['_delivery_zone'] = 'out-of-area';
        payload.config['_delivery_postcode'] = this._pendingOutOfArea.postcode;
        this._pendingOutOfArea = null;
      }

      // Copy line item properties into config (underscore-prefixed keys
      // already excluded so ops data doesn't leak to the customer surface).
      for (var key in props) {
        if (props.hasOwnProperty(key) && key.charAt(0) !== '_') {
          payload.config[key] = props[key];
        }
      }

      payload.config['Product'] = unitLabel(this._data.product.title, state);
      // Area is ops-only. Underscore-prefixed name keeps it out of the
      // buyer-facing quote summary while remaining visible to fulfilment.
      payload.config['_area_sqm'] = ((state.width * state.height) / 1000000).toFixed(2);

      if (phone) { payload.customer.phone = phone; payload.config['Phone'] = phone; }
      if (postcode) payload.config['Postcode'] = postcode;
      if (notes) payload.config['Notes'] = notes.substring(0, 500);

      // Attach photos
      var photos = State.getState().photos || [];
      if (photos.length > 0) {
        payload.photos = photos.map(function (p) { return { data: p.data }; });
        payload.config['Photos Attached'] = photos.length + ' photo(s)';
      }

      if (exceedsDeliveryMax(state.width, state.height)) {
        payload.tags.push('over-delivery-max');
      }

      var self = this;
      this._submitting = true;

      var submitBtn = modal.querySelector('button[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Submitting...';
      }

      fetchWithRetry(this._data.railwayApiUrl, payload)
      .then(function (data) {
        var form = document.getElementById('gc8-quote-form');
        var success = document.getElementById('gc8-quote-success');
        if (form) form.style.display = 'none';
        if (success) {
          success.style.display = 'block';
          var refEl = document.getElementById('gc8-quote-ref');
          if (refEl) refEl.textContent = data.draft_order_name || data.id || 'QR-' + Date.now();
        }

        GTM.push('configurator_quote_requested', {
          price: price,
          is_oversized: state.width > threshold || state.height > threshold
        });
      })
      .catch(function () {
        // All retries failed — persist to localStorage for later
        persistQuote(payload, self._data.railwayApiUrl);
        self.showQuoteError(
          'We couldn\u2019t reach our server right now, but your quote has been saved. ' +
          'It will be submitted automatically next time you visit, or call us on 0117\u00A0330\u00A03057.'
        );
      })
      .finally(function () {
        self._submitting = false;
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Submit Quote Request';
        }
      });
    },

    setButtonState: function (btnId, state, text) {
      var btn = document.getElementById(btnId);
      if (!btn) return;

      btn.classList.remove('pr-btn--loading', 'pr-btn--success', 'pr-btn--error');

      switch (state) {
        case 'loading':
          btn.classList.add('pr-btn--loading');
          btn.textContent = text;
          btn.disabled = true;
          break;
        case 'success':
          btn.classList.add('pr-btn--success');
          btn.textContent = text;
          btn.disabled = true;
          break;
        case 'error':
          btn.classList.add('pr-btn--error');
          btn.textContent = text;
          btn.disabled = false;
          break;
        default:
          btn.textContent = text;
          btn.disabled = false;
      }
    },

    showError: function (msg) {
      var bar = document.querySelector('.gc8__price-bar');
      if (!bar) return;

      var existing = bar.querySelector('.gc8__cart-error');
      if (existing) existing.parentNode.removeChild(existing);

      var el = document.createElement('div');
      el.className = 'gc8__cart-error';
      el.textContent = msg;
      bar.appendChild(el);
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 5000);
    },

    showQuoteError: function (msg) {
      var el = document.getElementById('gc8-quote-error');
      if (el) {
        el.textContent = msg;
        el.style.display = 'block';
        setTimeout(function () { el.style.display = 'none'; }, 6000);
      }
    }
  };


  /* ═══════════════════════════════════════════════════════════════════════════
   * 6. SHARE CONTROLLER
   * ═══════════════════════════════════════════════════════════════════════════ */

  var ShareController = {
    init: function () {
      var self = this;

      // Wire both share buttons (price bar + review step)
      var shareBtns = ['gc8-share-btn', 'gc8-share-btn-review'];
      for (var s = 0; s < shareBtns.length; s++) {
        var btn = document.getElementById(shareBtns[s]);
        if (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            self.copyShareLink();
          });
        }
      }
    },

    /** Encode current state as Base64 URL param. */
    encodeConfig: function (state) {
      var obj = {};
      if (state.width !== 1200) obj.w = state.width;
      if (state.height !== 1400) obj.h = state.height;
      if (state.glassType !== 'Standard') obj.g = state.glassType;
      if (state.outerPane && state.outerPane !== '4mm-clear') obj.op = state.outerPane;
      if (state.construction && state.construction !== 'double') obj.cn = state.construction;
      if (state.installType !== 'window') obj.i = state.installType;
      if (state.frameMaterial && state.frameMaterial !== 'upvc') obj.f = state.frameMaterial;
      if (state.toughened) obj.tg = 1;
      if (state.solar) obj.sl = 1;
      if (state.argon) obj.ar = 1;
      if (state.selfclean) obj.sc = 1;
      if (state.privacyGlass && state.privacyGlass !== 'none') obj.pv = state.privacyGlass;
      if (state.spacerType && state.spacerType !== 'standard') obj.stp = state.spacerType;
      if (state.spacerColour) obj.sp = state.spacerColour;
      if (state.performanceTier) obj.pt = state.performanceTier;
      if (state.cavityWidth) obj.cw = state.cavityWidth;
      if (state.laminated && state.laminated !== 'none') obj.lm = state.laminated;
      if (state.installType === 'conservatory' && state.conservatoryType) obj.ct = state.conservatoryType;
      if (state.georgianEnabled) {
        obj.ge = 1;
        obj.ga = state.georgianAcross;
        obj.gd = state.georgianDown;
        if (state.georgianWidth !== '18') obj.gw = state.georgianWidth;
        if (state.georgianColour !== 'white') obj.gc = state.georgianColour;
      }
      if (state.leadEnabled) {
        obj.le = 1;
        obj.lp = state.leadPattern;
      }
      if (state.catflap) {
        obj.cf = 1;
        if (state.catflapSize !== 'medium') obj.cs = state.catflapSize;
      }
      if (state.letterbox) obj.lb = 1;
      if (state.quantity > 1) obj.q = state.quantity;

      try {
        return btoa(JSON.stringify(obj));
      } catch (e) {
        return '';
      }
    },

    /** Decode Base64 URL param back to state updates. */
    decodeConfig: function (encoded) {
      try {
        var obj = JSON.parse(atob(encoded));
        var updates = {};

        if (obj.w) updates.width = parseInt(obj.w);
        if (obj.h) updates.height = parseInt(obj.h);
        if (obj.g) updates.glassType = obj.g;
        if (obj.op) updates.outerPane = obj.op;
        if (obj.cn) updates.construction = obj.cn;
        if (obj.i) updates.installType = obj.i;
        if (obj.f) updates.frameMaterial = obj.f;
        if (obj.tg) updates.toughened = true;
        if (obj.sl) updates.solar = true;
        if (obj.ar) updates.argon = true;
        if (obj.sc) updates.selfclean = true;
        if (obj.pv) updates.privacyGlass = obj.pv;
        if (obj.stp) updates.spacerType = obj.stp;
        if (obj.sp) updates.spacerColour = obj.sp;
        if (obj.pt) updates.performanceTier = obj.pt;
        if (obj.cw) updates.cavityWidth = obj.cw;
        if (obj.lm) updates.laminated = obj.lm;
        if (obj.ct) updates.conservatoryType = obj.ct;
        if (obj.ge) {
          updates.georgianEnabled = true;
          if (obj.ga) updates.georgianAcross = parseInt(obj.ga);
          if (obj.gd) updates.georgianDown = parseInt(obj.gd);
          if (obj.gw) updates.georgianWidth = obj.gw;
          if (obj.gc) updates.georgianColour = obj.gc;
        }
        if (obj.le) {
          updates.leadEnabled = true;
          if (obj.lp) updates.leadPattern = obj.lp;
        }
        if (obj.cf) {
          updates.catflap = true;
          if (obj.cs) updates.catflapSize = obj.cs;
        }
        if (obj.lb) updates.letterbox = true;
        if (obj.q) updates.quantity = parseInt(obj.q);

        return updates;
      } catch (e) {
        return null;
      }
    },

    /** Copy shareable URL to clipboard with toast feedback. */
    copyShareLink: function () {
      var state = State.getState();
      var encoded = this.encodeConfig(state);
      var url = window.location.origin + window.location.pathname + '?gc8=' + encoded;

      var self = this;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () {
          self.showToast('Link copied!');
        }).catch(function () {
          self.fallbackCopy(url);
        });
      } else {
        self.fallbackCopy(url);
      }

      GTM.push('configurator_config_shared', {});
    },

    fallbackCopy: function (text) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        this.showToast('Link copied!');
      } catch (e) {
        this.showToast('Copy link from address bar');
      }
      document.body.removeChild(ta);
    },

    showToast: function (msg) {
      // Remove existing toast
      var existing = document.querySelector('.gc8__share-toast');
      if (existing) existing.parentNode.removeChild(existing);

      var toast = document.createElement('div');
      toast.className = 'gc8__share-toast';
      toast.textContent = msg;
      document.body.appendChild(toast);

      // Force reflow then show
      toast.offsetHeight;
      toast.classList.add('gc8__share-toast--visible');

      setTimeout(function () {
        toast.classList.remove('gc8__share-toast--visible');
        setTimeout(function () {
          if (toast.parentNode) toast.parentNode.removeChild(toast);
        }, 300);
      }, 2000);
    },

    /** Check URL for ?gc8= param and restore state on load. */
    restoreFromUrl: function () {
      var params = new URLSearchParams(window.location.search);
      var encoded = params.get('gc8');
      if (!encoded) return false;

      var updates = this.decodeConfig(encoded);
      if (updates) {
        State.setMany(updates);
        return true;
      }
      return false;
    }
  };


  /* ═══════════════════════════════════════════════════════════════════════════
   * 7. TRADE MODE
   * ═══════════════════════════════════════════════════════════════════════════ */

  var TradeMode = {
    _active: false,

    init: function () {
      var self = this;

      // Check localStorage for saved preference
      try {
        this._active = localStorage.getItem('gc8-trade-mode') === 'true';
      } catch (e) { /* private browsing */ }

      // Apply immediately (before first paint) to avoid FOUC
      if (this._active) {
        this.apply(true);
      }

      // Bind toggle
      var toggle = document.getElementById('gc8-trade-toggle');
      if (toggle) {
        toggle.checked = this._active;
        toggle.addEventListener('change', function () {
          self._active = toggle.checked;
          self.apply(self._active);
          try {
            localStorage.setItem('gc8-trade-mode', self._active ? 'true' : 'false');
          } catch (e) { /* quota */ }

          GTM.push('configurator_trade_mode_toggled', { trade_mode: self._active });
        });
      }
    },

    apply: function (isActive) {
      var wrapper = document.querySelector('.gc8');
      if (wrapper) {
        if (isActive) {
          wrapper.classList.add('gc8--trade');
        } else {
          wrapper.classList.remove('gc8--trade');
        }
      }
    },

    isActive: function () {
      return this._active;
    }
  };


  /* ═══════════════════════════════════════════════════════════════════════════
   * 8. DISPATCH ESTIMATOR
   * ═══════════════════════════════════════════════════════════════════════════ */

  var DispatchEstimator = {
    _data: null,

    init: function (data) {
      this._data = data;
      this.update();

      // Re-estimate on price updates (dimensions/options change)
      var self = this;
      document.addEventListener('gc8:price-update', function () {
        self.update();
      });
    },

    getEstimate: function (state) {
      var text = leadTimeText(state);
      var cssClass = text === 'Quote required' ? 'gc8__dispatch--oversized'
        : (text.indexOf('5') === 0 ? 'gc8__dispatch--fast' : 'gc8__dispatch--standard');
      return { text: text, cssClass: cssClass };
    },

    update: function () {
      var state = State.getState();
      var estimate = this.getEstimate(state);

      var el = document.querySelector('.gc8__dispatch-text');
      if (el) {
        el.textContent = estimate.text;
        el.className = 'gc8__dispatch-text ' + estimate.cssClass;
      }
    }
  };


  /* ═══════════════════════════════════════════════════════════════════════════
   * 9. GTM / ANALYTICS
   * ═══════════════════════════════════════════════════════════════════════════ */

  var GTM = {
    _data: null,
    _attribution: null,

    init: function (data) {
      this._data = data;
      window.dataLayer = window.dataLayer || [];

      // Capture deep-link attribution from the inline capture script in
      // sections/configurator-v8.liquid. These are stamped onto every payload
      // via push() so downstream GA4/GTM joins on source_page / source_topic.
      this._attribution = {
        source_page: (typeof window !== 'undefined' && window.PR_V8_FROM_PAGE) || null,
        source_topic: (typeof window !== 'undefined' && window.PR_V8_REQUESTED_TOPIC) || null,
        source_context: (typeof window !== 'undefined' && window.PR_V8_REQUESTED_CONTEXT) || null,
        requested_product: (typeof window !== 'undefined' && window.PR_V8_REQUESTED_PRODUCT) || null
      };

      this.push('configurator_loaded', {
        product_handle: data.product.handle,
        product_title: data.product.title
      });

      // Emit a dedicated attribution event when arriving from a topical page
      // or product page deep link, so the funnel start is timestamped on its
      // own row (configurator_loaded is too generic to filter on cleanly).
      if (this._attribution.source_page || this._attribution.source_topic || this._attribution.requested_product) {
        this.push('configurator_deep_link_attribution', {});
      }

      var self = this;

      // Step views
      document.addEventListener('gc8:step-change', function (e) {
        self.push('configurator_step_viewed', {
          step_number: e.detail.step,
          step_name: e.detail.stepName || ''
        });
      });

      // Option selections (debounced)
      document.addEventListener('gc8:config-change', debounce(function () {
        var state = State.getState();
        self.push('configurator_option_selected', {
          install_type: state.installType,
          glass_type: state.glassType,
          width_mm: state.width,
          height_mm: state.height
        });
      }, 500));

      // Price calculations
      document.addEventListener('gc8:price-update', function (e) {
        var price = e.detail ? e.detail.unitPrice : 0;
        var state = State.getState();
        self.push('configurator_price_calculated', {
          price: price,
          currency: 'GBP',
          area_sqm: ((state.width * state.height) / 1000000).toFixed(2)
        });
      });
    },

    push: function (eventName, params) {
      var payload = {
        event: eventName,
        configurator_version: 'v8',
        product_handle: this._data ? this._data.product.handle : ''
      };

      // Stamp deep-link attribution onto every event so downstream funnels
      // can attribute conversions back to the source page/topic/context
      // without having to join on the initial attribution event.
      if (this._attribution) {
        if (this._attribution.source_page) payload.source_page = this._attribution.source_page;
        if (this._attribution.source_topic) payload.source_topic = this._attribution.source_topic;
        if (this._attribution.source_context) payload.source_context = this._attribution.source_context;
        if (this._attribution.requested_product) payload.requested_product = this._attribution.requested_product;
      }

      for (var key in params) {
        if (params.hasOwnProperty(key)) {
          payload[key] = params[key];
        }
      }

      window.dataLayer.push(payload);
    }
  };


  /* ═══════════════════════════════════════════════════════════════════════════
   * PHOTO UPLOAD
   * ═══════════════════════════════════════════════════════════════════════════ */

  var PhotoUpload = {
    MAX_PHOTOS: 3,
    MAX_FILE_SIZE: 10 * 1024 * 1024,
    MAX_DIMENSION: 1200,
    QUALITY: 0.8,

    init: function () {
      var zone = document.getElementById('gc8-upload-zone');
      var input = document.getElementById('gc8-photos');
      if (!zone || !input) return;

      var self = this;

      // Click to browse
      zone.addEventListener('click', function () { input.click(); });
      zone.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); }
      });

      // File selection
      input.addEventListener('change', function () {
        self.handleFiles(input.files);
        input.value = '';
      });

      // Drag and drop
      zone.addEventListener('dragover', function (e) {
        e.preventDefault();
        zone.classList.add('gc8__upload-zone--dragover');
      });
      zone.addEventListener('dragleave', function () {
        zone.classList.remove('gc8__upload-zone--dragover');
      });
      zone.addEventListener('drop', function (e) {
        e.preventDefault();
        zone.classList.remove('gc8__upload-zone--dragover');
        self.handleFiles(e.dataTransfer.files);
      });

      // Update prompt text when georgian bars toggle
      State.subscribe(function (key) {
        if (key === 'georgianEnabled' || key === 'leadEnabled') {
          self.updatePrompt();
        }
      });
    },

    updatePrompt: function () {
      var state = State.getState();
      var promptBar = document.getElementById('gc8-photo-prompt-bar');
      var promptText = document.getElementById('gc8-photo-prompt-text');
      if (state.georgianEnabled || state.leadEnabled) {
        if (promptBar) promptBar.style.display = 'flex';
        if (promptText) promptText.textContent = 'Reference photo for bar pattern matching';
      } else {
        if (promptBar) promptBar.style.display = 'none';
        if (promptText) promptText.textContent = 'Add a reference photo (optional)';
      }
    },

    handleFiles: function (fileList) {
      var photos = State.getState().photos || [];
      var remaining = this.MAX_PHOTOS - photos.length;
      if (remaining <= 0) {
        this.showError('Maximum ' + this.MAX_PHOTOS + ' photos allowed.');
        return;
      }

      var files = Array.prototype.slice.call(fileList, 0, remaining);
      var errorEl = document.getElementById('gc8-photo-error');
      if (errorEl) errorEl.textContent = '';

      for (var i = 0; i < files.length; i++) {
        var file = files[i];
        if (!file.type.startsWith('image/')) {
          this.showError('Only image files are accepted.');
          continue;
        }
        if (file.size > this.MAX_FILE_SIZE) {
          this.showError(file.name + ' is too large (max 10MB).');
          continue;
        }
        this.compressAndAdd(file);
      }
    },

    compressAndAdd: function (file) {
      var self = this;
      var progress = document.getElementById('gc8-upload-progress');
      if (progress) progress.style.display = 'flex';

      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          var canvas = document.createElement('canvas');
          var scale = Math.min(1, self.MAX_DIMENSION / Math.max(img.width, img.height));
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          var dataUrl = canvas.toDataURL('image/jpeg', self.QUALITY);

          var photos = (State.getState().photos || []).slice();
          photos.push({ data: dataUrl, name: file.name });
          State.setState('photos', photos);
          self.renderPreviews();

          if (progress) progress.style.display = 'none';
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    removePhoto: function (index) {
      var photos = (State.getState().photos || []).slice();
      photos.splice(index, 1);
      State.setState('photos', photos);
      this.renderPreviews();
    },

    renderPreviews: function () {
      var container = document.getElementById('gc8-upload-previews');
      if (!container) return;
      container.innerHTML = '';

      var photos = State.getState().photos || [];
      var self = this;

      for (var i = 0; i < photos.length; i++) {
        (function (idx) {
          var thumb = document.createElement('div');
          thumb.className = 'gc8__upload-thumb';
          thumb.innerHTML = '<img src="' + photos[idx].data + '" alt="Reference photo ' + (idx + 1) + '">' +
            '<button type="button" class="gc8__upload-remove" aria-label="Remove photo ' + (idx + 1) + '">\u00D7</button>';
          thumb.querySelector('.gc8__upload-remove').addEventListener('click', function () {
            self.removePhoto(idx);
          });
          container.appendChild(thumb);
        })(i);
      }

      // Update zone visibility
      var zone = document.getElementById('gc8-upload-zone');
      if (zone) zone.style.display = photos.length >= this.MAX_PHOTOS ? 'none' : '';
    },

    showError: function (msg) {
      var el = document.getElementById('gc8-photo-error');
      if (el) el.textContent = msg;
    }
  };


  /* ═══════════════════════════════════════════════════════════════════════════
   * 10. INIT — ENTRY POINT
   * ═══════════════════════════════════════════════════════════════════════════ */

  function init() {
    // Read JSON data layer
    var dataScript = document.getElementById('gc8-data');
    if (!dataScript) return;

    var data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }

    if (!data || !data.product) return;

    applySiteFacts(data.facts);

    // ── 0a. Deep-link product pre-select (from product page or topical CTA) ──
    // window.PR_V8_REQUESTED_PRODUCT is set by the inline capture script in
    // sections/configurator-v8.liquid when the page is loaded with ?product=<handle>.
    // We fetch /products/{handle}.js and swap product identity fields (id, title,
    // handle, url, media) before any module initialises, so breadcrumbs / return-link
    // / analytics reflect the source product the buyer clicked from. Pricing,
    // option metaobjects, and variants stay bound to the canonical configurator
    // product (those are the configurator's pricing model, not the source product's).
    var requested = (typeof window !== 'undefined') ? window.PR_V8_REQUESTED_PRODUCT : null;
    if (requested && requested !== data.product.handle) {
      fetch('/products/' + encodeURIComponent(requested) + '.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (p && p.handle) {
            data.product.id = p.id;
            data.product.title = p.title;
            data.product.handle = p.handle;
            data.product.url = p.url;
            if (p.media && p.media.length) {
              data.product.media = p.media.map(function (m) {
                var raw = m.src || m.preview_image && m.preview_image.src || '';
                return {
                  id: m.id,
                  type: m.media_type || 'image',
                  src: _resizeShopifyImg(raw, 800),
                  thumb: _resizeShopifyImg(raw, 120),
                  alt: m.alt || p.title
                };
              });
            }
          }
        })
        .catch(function () { /* network/404 — fall through with canonical default */ })
        .then(function () { _continueInit(data); });
      return;
    }

    _continueInit(data);
  }

  // Inject Shopify CDN size suffix (_NNNx) before the file extension.
  function _resizeShopifyImg(url, width) {
    if (!url) return url;
    return url.replace(/(\.(?:jpe?g|png|gif|webp|avif))(\?|$)/i, '_' + width + 'x$1$2');
  }

  function _continueInit(data) {
    // ── 0. Set API key and flush saved quotes ──
    if (data.railwayApiKey) setApiKey(data.railwayApiKey);
    flushPendingQuotes();

    // ── 1. Initialise state ──
    State.init(data);

    // ── 1a. Deep-link install_type pre-select ──
    // The product-page Configure CTA passes ?install_type={door|window|skylight
    // |conservatory} so step 1 lands pre-selected instead of dropping the buyer
    // on the default window radio. Captured into window.PR_V8_REQUESTED_INSTALL_TYPE
    // by the inline script in sections/configurator-v8.liquid. Apply BEFORE
    // localStorage restore (#2) so any saved-state install_type stays the
    // source of truth for returning buyers; the deep-link only overrides when
    // there is no localStorage state to honour.
    var requestedInstall = (typeof window !== 'undefined') ? window.PR_V8_REQUESTED_INSTALL_TYPE : null;
    if (requestedInstall) {
      var _allowedInstalls = ['window', 'door', 'skylight', 'conservatory'];
      if (_allowedInstalls.indexOf(requestedInstall) !== -1) {
        State.setState('installType', requestedInstall);
      }
    }

    // ── 2. Load saved state from localStorage ──
    var restored = State.loadState();

    // ── 3. Check URL params for shared config (overrides localStorage) ──
    ShareController.restoreFromUrl();

    // ── 4. Apply trade mode before first render ──
    TradeMode.init();

    // ── 5. Sync DOM from state (always — ensures HTML defaults match JS state) ──
    syncDomFromState(data);

    // ── 6. Initialise modules ──
    WizardController.init(data);
    PricingEngine.recalculate(data);
    CheckoutController.init(data);
    BottomSheetController.init();
    ShareController.init();
    DispatchEstimator.init(data);
    GTM.init(data);
    PhotoUpload.init();

    // ── 7. Wire form field listeners ──
    wireFieldListeners(data);

    // ── 8. Wire quantity controls ──
    wireQtyControls(data);

    // ── 9. Subscribe to state changes ──
    // applyBusinessRules can itself setState (auto-toughened), which re-enters
    // this subscriber. Rather than run the full recalc + DOM pass 2–3× per
    // change, coalesce: run to a fixed point (so the toughened price IS picked
    // up) but dispatch + save only ONCE at the end.
    var saveTimer = null;
    var ruleRunning = false;
    var ruleDirty = false;
    var lastKey = null;
    State.subscribe(function (key) {
      lastKey = key;
      if (ruleRunning) { ruleDirty = true; return; }
      ruleRunning = true;
      var guard = 0;
      do {
        ruleDirty = false;
        PricingEngine.recalculate(data);   // price
        WizardController.applyBusinessRules(); // may setState → sets ruleDirty
      } while (ruleDirty && ++guard < 5);
      ruleRunning = false;

      // Auto-save to localStorage (debounced)
      clearTimeout(saveTimer);
      saveTimer = setTimeout(function () {
        State.saveState();
      }, 500);

      // Dispatch generic config-change for canvas/external listeners (once)
      document.dispatchEvent(new CustomEvent('gc8:config-change', {
        detail: { key: lastKey, state: State.getState() }
      }));
    });

    // ── 9b. Boot the canvas + fire initial config-change so it draws frame 1 ──
    // setTimeout(0) ensures canvas.js has bound its listeners (both scripts use defer).
    // We dispatch gc8:canvas-init explicitly rather than rely on canvas.js's
    // DOMContentLoaded fallback (which depended on script-evaluation ordering).
    setTimeout(function () {
      document.dispatchEvent(new CustomEvent('gc8:canvas-init'));
      document.dispatchEvent(new CustomEvent('gc8:config-change', {
        detail: { key: '__init__', state: State.getState() }
      }));
    }, 0);

    // ── 10. Sync canvas drag-to-resize back into form + State ──
    document.addEventListener('gc8:canvas-drag', function (e) {
      var d = e.detail || {};
      if (d.width != null) {
        var wEl = document.querySelector('[data-gc8-field="width"]');
        if (wEl) { wEl.value = d.width; wEl.dispatchEvent(new Event('input', { bubbles: true })); }
      }
      if (d.height != null) {
        var hEl = document.querySelector('[data-gc8-field="height"]');
        if (hEl) { hEl.value = d.height; hEl.dispatchEvent(new Event('input', { bubbles: true })); }
      }
    });

    // ── 11. Pre-fill from URL params (from homepage hero modal) ──
    (function () {
      var params = new URLSearchParams(window.location.search);
      var install = params.get('install');
      var w = params.get('w');
      var h = params.get('h');
      if (!install && !w) return;

      if (install) {
        var installEl = document.querySelector('[data-gc8-field="installType"][value="' + install + '"]');
        if (installEl) { installEl.checked = true; installEl.dispatchEvent(new Event('change', { bubbles: true })); }
      }
      if (w) {
        var wEl = document.querySelector('[data-gc8-field="width"]');
        if (wEl) { wEl.value = w; wEl.dispatchEvent(new Event('input', { bubbles: true })); }
      }
      if (h) {
        var hEl = document.querySelector('[data-gc8-field="height"]');
        if (hEl) { hEl.value = h; hEl.dispatchEvent(new Event('input', { bubbles: true })); }
      }

      // Auto-advance to Step 2 if all basics are filled
      if (install && w && h) {
        setTimeout(function () {
          var nextBtn = document.querySelector('[data-gc8-nav="next"]');
          if (nextBtn) nextBtn.click();
        }, 500);
      }
    })();

    // ── 12. Announce ready ──
    document.dispatchEvent(new CustomEvent('gc8:ready', {
      detail: { product: data.product }
    }));
  }


  /* ═══════════════════════════════════════════════════════════════════════════
   * FORM FIELD WIRING
   * ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Wire change/input events on all data-gc8-field elements to update State.
   */
  function wireFieldListeners(data) {
    // Radio card / radio chip fields
    var radioFields = ['installType', 'conservatoryType', 'frameMaterial', 'glassType', 'outerPane',
                       'construction', 'performanceTier', 'privacyGlass', 'cavityWidth',
                       'spacerType', 'spacerColour', 'laminated', 'georgianColour', 'georgianWidth',
                       'leadPattern', 'georgianSpacingMode'];

    for (var r = 0; r < radioFields.length; r++) {
      wireRadioField(radioFields[r], data);
    }

    // Double/Triple toggle: only reveal once the Sheet has triple pricing, and
    // surface the frame-fit note when triple is chosen.
    var consFs = document.querySelector('[data-gc8-construction]');
    if (consFs) {
      if (hasTriplePricing(data)) consFs.hidden = false;
      var consNote = consFs.querySelector('[data-gc8-construction-note]');
      var consInputs = consFs.querySelectorAll('input[name="gc8-construction"]');
      for (var ci = 0; ci < consInputs.length; ci++) {
        consInputs[ci].addEventListener('change', function () {
          if (consNote) consNote.hidden = (State.getState().construction !== 'triple');
        });
      }
    }

    // Spacer type → colour dependency: Super Spacer only comes in black or white,
    // so hide the standard-only swatches (silver/brown/gold) when it's selected
    // and reset the colour if one of those was chosen.
    var spacerTypeInputs = document.querySelectorAll('input[name="gc8-spacer-type"]');
    function applySpacerType() {
      var isSuper = State.getState().spacerType === 'super';
      var standardOnly = document.querySelectorAll('[data-spacer-standard-only]');
      for (var i = 0; i < standardOnly.length; i++) standardOnly[i].hidden = isSuper;
      if (isSuper) {
        var c = State.getState().spacerColour;
        if (c !== 'black' && c !== 'white') {
          State.setState('spacerColour', 'black');
          syncRadio('spacerColour', 'black');
        }
      }
    }
    for (var si = 0; si < spacerTypeInputs.length; si++) {
      spacerTypeInputs[si].addEventListener('change', applySpacerType);
    }
    if (spacerTypeInputs.length) applySpacerType();

    // Privacy glass disclosure: keep the 14 obscure patterns collapsed (clear is
    // the default for most buyers) and reveal them only on request. Unchecking
    // resets back to clear/none.
    var privToggle = document.querySelector('[data-gc8-privacy-toggle]');
    var privGrid = document.querySelector('[data-gc8-privacy-grid]');
    if (privToggle && privGrid) {
      var pv = State.getState().privacyGlass;
      if (pv && pv !== 'none') privToggle.checked = true; // restored from URL
      privGrid.hidden = !privToggle.checked;
      privToggle.addEventListener('change', function () {
        privGrid.hidden = !privToggle.checked;
        if (!privToggle.checked) {
          var radios = document.querySelectorAll('input[name="gc8-privacy"]');
          for (var i = 0; i < radios.length; i++) radios[i].checked = false;
          State.setState('privacyGlass', 'none');
          State.setState('privacyPrice', 0);
        }
      });
    }

    // Live £ amounts on the upgrade toggles — clearer than "%" or "rate". Recomputed
    // on any state change (they depend on glass spec + unit size).
    function rateFor(toughened) {
      return deriveSheetRate({ glassType: State.getState().glassType, privacyGlass: State.getState().privacyGlass, laminated: State.getState().laminated, toughened: toughened }, data);
    }
    function updateUpgradePrices() {
      var s = State.getState();
      var area = ((s.width || 1200) * (s.height || 1400)) / 1000000;
      var argonEl = document.getElementById('gc8-argon-price');
      if (argonEl) {
        var aRate = sheetExtraRate(data, 'argon');
        if (aRate == null) aRate = 6;
        argonEl.textContent = '+£' + Math.round(aRate * area);
      }
      var toughEl = document.getElementById('gc8-toughened-price');
      if (toughEl) {
        var rNon = rateFor(false), rT = rateFor(true);
        if (rNon && rT && rNon.rate != null && rT.rate != null) {
          toughEl.textContent = '+£' + Math.round((rT.rate - rNon.rate) * area);
        }
      }
    }
    State.subscribe(function () { updateUpgradePrices(); });
    updateUpgradePrices();

    // Number inputs
    wireNumberField('width');
    wireNumberField('height');
    wireNumberField('georgianAcross');
    wireNumberField('georgianDown');

    // Rebuild ¢ position inputs when grid count changes
    State.subscribe(function (key) {
      if (key === 'georgianAcross' || key === 'georgianDown' || key === 'georgianSpacingMode' || key === 'georgianEnabled') {
        wireGeorgianPositionInputs();
        validateBarPositions();
        // Toggle visibility of custom spacing section
        var customSection = document.getElementById('gc8-georgian-custom-spacing');
        if (customSection) {
          var state = State.getState();
          customSection.style.display = (state.georgianEnabled && state.georgianSpacingMode === 'custom') ? 'block' : 'none';
        }
      }
    });

    State.subscribe(function (key) {
      if (key === 'georgianVPositions' || key === 'georgianHPositions') {
        validateBarPositions();
      }
    });

    // Toggle / checkbox fields
    var toggleFields = ['toughened', 'solar', 'argon', 'selfclean',
                        'georgianEnabled', 'leadEnabled', 'catflap', 'letterbox'];

    for (var t = 0; t < toggleFields.length; t++) {
      wireToggleField(toggleFields[t], data);
    }

    // Select fields
    var catflapSizeEl = document.querySelector('[data-gc8-field="catflapSize"]');
    if (catflapSizeEl) {
      catflapSizeEl.addEventListener('change', function () {
        State.setState('catflapSize', catflapSizeEl.value);
      });
    }

    // Textarea
    var specialEl = document.querySelector('[data-gc8-field="specialRequests"]');
    if (specialEl) {
      specialEl.addEventListener('input', debounce(function () {
        State.setState('specialRequests', specialEl.value);
      }, 300));
    }
  }

  /**
   * Wire the dynamic Georgian bar ¢ position inputs.
   * Called on init and whenever georgianAcross/georgianDown changes.
   */
  function wireGeorgianPositionInputs() {
    var state = State.getState();
    var vCount = Math.max(0, (state.georgianAcross || 2) - 1);
    var hCount = Math.max(0, (state.georgianDown || 2) - 1);
    var barWidth = parseInt(state.georgianWidth, 10) || 18;

    // Build position arrays pre-filled with equal spacing
    var vPositions = [];
    var hPositions = [];
    var oldV = state.georgianVPositions || [];
    var oldH = state.georgianHPositions || [];

    for (var i = 0; i < vCount; i++) {
      vPositions.push(oldV[i] != null ? oldV[i] : Math.round(state.width / (vCount + 1) * (i + 1)));
    }
    for (var j = 0; j < hCount; j++) {
      hPositions.push(oldH[j] != null ? oldH[j] : Math.round(state.height / (hCount + 1) * (j + 1)));
    }

    State.setState('georgianVPositions', vPositions);
    State.setState('georgianHPositions', hPositions);

    // Render the input fields into the container
    var vContainer = document.getElementById('gc8-georgian-v-inputs');
    var hContainer = document.getElementById('gc8-georgian-h-inputs');
    if (!vContainer || !hContainer) return;

    vContainer.innerHTML = '';
    hContainer.innerHTML = '';

    for (var vi = 0; vi < vCount; vi++) {
      vContainer.appendChild(createPositionInput('v', vi, vPositions[vi], state.width, 'left'));
    }
    for (var hi = 0; hi < hCount; hi++) {
      hContainer.appendChild(createPositionInput('h', hi, hPositions[hi], state.height, 'top'));
    }
  }

  function createPositionInput(axis, index, value, max, edgeLabel) {
    var div = document.createElement('div');
    div.className = 'gc8__position-field';

    var label = document.createElement('label');
    label.className = 'gc8__field-label';
    label.textContent = '\u00A2 Bar ' + (index + 1);
    label.setAttribute('for', 'gc8-pos-' + axis + '-' + index);

    var wrap = document.createElement('div');
    wrap.className = 'gc8__position-input-wrap';

    var input = document.createElement('input');
    input.type = 'number';
    input.id = 'gc8-pos-' + axis + '-' + index;
    input.className = 'pr-input pr-input--sm';
    input.value = value;
    input.min = 1;
    input.max = max - 1;
    input.setAttribute('data-gc8-pos-axis', axis);
    input.setAttribute('data-gc8-pos-index', index);

    var suffix = document.createElement('span');
    suffix.className = 'gc8__position-suffix';
    suffix.textContent = 'mm from ' + edgeLabel + ' edge';

    wrap.appendChild(input);
    wrap.appendChild(suffix);
    div.appendChild(label);
    div.appendChild(wrap);

    // Wire input event
    input.addEventListener('input', function () {
      var val = parseFloat(input.value) || 0;
      var positions = State.getState()[axis === 'v' ? 'georgianVPositions' : 'georgianHPositions'].slice();
      positions[index] = val;
      State.setState(axis === 'v' ? 'georgianVPositions' : 'georgianHPositions', positions);
    });

    return div;
  }

  function validateBarPositions() {
    var state = State.getState();
    if (!state.georgianEnabled || state.georgianSpacingMode !== 'custom') return;

    var barWidth = parseInt(state.georgianWidth, 10) || 18;
    var vPos = (state.georgianVPositions || []).slice().sort(function (a, b) { return a - b; });
    var hPos = (state.georgianHPositions || []).slice().sort(function (a, b) { return a - b; });
    var hasWarning = false;

    // Validate individual inputs
    var inputs = document.querySelectorAll('[data-gc8-pos-axis]');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var axis = input.getAttribute('data-gc8-pos-axis');
      var val = parseFloat(input.value) || 0;
      var max = axis === 'v' ? state.width : state.height;
      var invalid = val < barWidth / 2 || val > max - barWidth / 2;
      input.classList.toggle('pr-input--error', invalid);
    }

    // Check for uneven spacing warning
    function checkUneven(positions, total) {
      if (positions.length < 1) return false;
      var edges = [0].concat(positions).concat([total]);
      var gaps = [];
      for (var g = 1; g < edges.length; g++) {
        gaps.push(edges[g] - edges[g - 1]);
      }
      var avg = total / gaps.length;
      for (var gi = 0; gi < gaps.length; gi++) {
        if (Math.abs(gaps[gi] - avg) / avg > 0.2) return true;
      }
      return false;
    }

    hasWarning = checkUneven(vPos, state.width) || checkUneven(hPos, state.height);

    var warningEl = document.getElementById('gc8-spacing-warning');
    if (warningEl) warningEl.style.display = hasWarning ? 'flex' : 'none';
  }

  function wireRadioField(fieldName, data) {
    var els = document.querySelectorAll('[data-gc8-field="' + fieldName + '"]');
    for (var i = 0; i < els.length; i++) {
      els[i].addEventListener('change', function () {
        var val = this.value;
        State.setState(fieldName, val);

        // Read associated price/percent data attributes from the checked element
        // For SELECT elements, read from the selected option, not the select itself
        var attrSource = this;
        if (this.tagName === 'SELECT' && this.selectedOptions && this.selectedOptions.length) {
          attrSource = this.selectedOptions[0];
        }
        var priceAttr = parseFloat(attrSource.getAttribute('data-price')) || 0;
        var pctAttr = parseInt(attrSource.getAttribute('data-price-percent')) || 0;
        var priceSqmAttr = parseFloat(attrSource.getAttribute('data-price-sqm')) || 0;

        // Map field-specific price attributes to state
        switch (fieldName) {
          case 'glassType':
            State.setState('glassPrice', priceAttr);
            break;
          case 'performanceTier':
            State.setState('performancePricePercent', pctAttr);
            break;
          case 'privacyGlass':
            State.setState('privacyPrice', priceAttr);
            break;
          case 'cavityWidth':
            State.setState('cavityPrice', priceAttr);
            break;
          case 'laminated':
            State.setState('laminatedPriceSqm', priceSqmAttr);
            break;
        }
      });
    }
  }

  function wireNumberField(fieldName) {
    var el = document.querySelector('[data-gc8-field="' + fieldName + '"]');
    if (!el) return;

    var handler = debounce(function () {
      var val = parseFloat(el.value) || 0;
      State.setState(fieldName, val);
    }, 150);

    el.addEventListener('input', function () {
      // Clear error state on input
      el.classList.remove('pr-input--error');
      var errEl = document.getElementById('gc8-' + fieldName + '-error');
      if (errEl) errEl.textContent = '';
      handler();
    });
    el.addEventListener('change', handler);
  }

  function wireToggleField(fieldName, data) {
    var el = document.querySelector('[data-gc8-field="' + fieldName + '"]');
    if (!el) return;

    el.addEventListener('change', function () {
      State.setState(fieldName, el.checked);

      // Read percent data attribute for multiplier toggles
      var pctAttr = parseInt(el.getAttribute('data-price-percent')) || 0;
      switch (fieldName) {
        case 'toughened':
          State.setState('toughenedPercent', pctAttr || 15);
          break;
        case 'solar':
          State.setState('solarPercent', pctAttr || 20);
          break;
        case 'argon':
          State.setState('argonPercent', pctAttr || 15);
          break;
      }

      // Show/hide georgian config panel — mutual exclusion with lead
      if (fieldName === 'georgianEnabled') {
        var panel = document.querySelector('.gc8__georgian-config');
        if (panel) panel.style.display = el.checked ? 'block' : 'none';
        var barsLeadNote = document.getElementById('gc8-bars-lead-conflict');
        if (el.checked) {
          var leadCb = document.getElementById('gc8-lead-enable');
          if (leadCb && leadCb.checked) {
            leadCb.checked = false;
            State.setState('leadEnabled', false);
            var lp = document.getElementById('gc8-lead-config');
            if (lp) lp.style.display = 'none';
            if (barsLeadNote) barsLeadNote.style.display = 'flex';
            setTimeout(function () { if (barsLeadNote) barsLeadNote.style.display = 'none'; }, 4000);
          }
        } else {
          if (barsLeadNote) barsLeadNote.style.display = 'none';
        }
      }

      // Show/hide lead config panel — mutual exclusion with georgian
      if (fieldName === 'leadEnabled') {
        var leadPanel = document.getElementById('gc8-lead-config');
        if (leadPanel) leadPanel.style.display = el.checked ? 'block' : 'none';
        var leadBarsNote = document.getElementById('gc8-lead-bars-conflict');
        if (el.checked) {
          var georCb = document.getElementById('gc8-georgian-enable');
          if (georCb && georCb.checked) {
            georCb.checked = false;
            State.setState('georgianEnabled', false);
            var gp = document.querySelector('.gc8__georgian-config');
            if (gp) gp.style.display = 'none';
            if (leadBarsNote) leadBarsNote.style.display = 'flex';
            setTimeout(function () { if (leadBarsNote) leadBarsNote.style.display = 'none'; }, 4000);
          }
        } else {
          if (leadBarsNote) leadBarsNote.style.display = 'none';
        }
      }

      // Show/hide catflap config panel
      if (fieldName === 'catflap') {
        var catflapPanel = document.getElementById('gc8-catflap-config');
        if (catflapPanel) catflapPanel.style.display = el.checked ? 'block' : 'none';
      }

    });
  }


  /* ═══════════════════════════════════════════════════════════════════════════
   * QUANTITY CONTROLS
   * ═══════════════════════════════════════════════════════════════════════════ */

  function wireQtyControls(data) {
    // Sync all qty inputs when state changes
    function syncAllQty(val) {
      var inputs = document.querySelectorAll('#gc8-qty, #gc8-qty-review');
      for (var i = 0; i < inputs.length; i++) {
        inputs[i].value = val;
      }
    }

    // Wire all qty +/- buttons (price bar + review step)
    var minusBtns = document.querySelectorAll('[data-gc8-qty="minus"]');
    var plusBtns = document.querySelectorAll('[data-gc8-qty="plus"]');

    for (var m = 0; m < minusBtns.length; m++) {
      minusBtns[m].addEventListener('click', function () {
        var current = State.getState().quantity || 1;
        var val = Math.max(1, current - 1);
        syncAllQty(val);
        State.setState('quantity', val);
      });
    }

    for (var p = 0; p < plusBtns.length; p++) {
      plusBtns[p].addEventListener('click', function () {
        var current = State.getState().quantity || 1;
        var val = Math.min(50, current + 1);
        syncAllQty(val);
        State.setState('quantity', val);
      });
    }

    // Wire all qty inputs for manual entry
    var qtyInputs = document.querySelectorAll('#gc8-qty, #gc8-qty-review');
    for (var q = 0; q < qtyInputs.length; q++) {
      qtyInputs[q].addEventListener('change', function () {
        var val = parseInt(this.value) || 1;
        val = Math.max(1, Math.min(50, val));
        syncAllQty(val);
        State.setState('quantity', val);
      });
    }
  }


  /* ═══════════════════════════════════════════════════════════════════════════
   * DOM SYNC (restore saved state into form elements)
   * ═══════════════════════════════════════════════════════════════════════════ */

  function syncDomFromState(data) {
    var state = State.getState();

    // Radio fields — check the matching option
    syncRadio('installType', state.installType);
    syncRadio('conservatoryType', state.conservatoryType);
    syncRadio('frameMaterial', state.frameMaterial);
    syncRadio('glassType', state.glassType);
    syncRadio('outerPane', state.outerPane);
    syncRadio('construction', state.construction);
    syncRadio('performanceTier', state.performanceTier);
    syncRadio('privacyGlass', state.privacyGlass);
    syncRadio('cavityWidth', state.cavityWidth);
    syncRadio('spacerType', state.spacerType);
    syncRadio('spacerColour', state.spacerColour);
    syncRadio('laminated', state.laminated);

    // Number fields
    syncNumber('width', state.width);
    syncNumber('height', state.height);
    syncNumber('georgianAcross', state.georgianAcross);
    syncNumber('georgianDown', state.georgianDown);

    // Checkboxes
    syncCheckbox('toughened', state.toughened);
    syncCheckbox('solar', state.solar);
    syncCheckbox('argon', state.argon);
    syncCheckbox('selfclean', state.selfclean);
    syncCheckbox('georgianEnabled', state.georgianEnabled);
    syncRadio('georgianColour', state.georgianColour);
    syncRadio('georgianWidth', state.georgianWidth);
    syncCheckbox('leadEnabled', state.leadEnabled);
    syncRadio('leadPattern', state.leadPattern);
    syncCheckbox('catflap', state.catflap);
    syncCheckbox('letterbox', state.letterbox);

    // Select fields
    var catflapSizeEl = document.querySelector('[data-gc8-field="catflapSize"]');
    if (catflapSizeEl && state.catflapSize) catflapSizeEl.value = state.catflapSize;

    // Textarea
    var specialEl = document.querySelector('[data-gc8-field="specialRequests"]');
    if (specialEl && state.specialRequests) specialEl.value = state.specialRequests;

    // Quantity
    var qtyInput = document.getElementById('gc8-qty');
    if (qtyInput && state.quantity) qtyInput.value = state.quantity;

    // Show/hide conditional panels
    var conservConfig = document.querySelector('.gc8__conservatory-config');
    if (conservConfig) {
      conservConfig.style.display = state.installType === 'conservatory' ? 'block' : 'none';
    }

    var georgianPanel = document.querySelector('.gc8__georgian-config');
    if (georgianPanel) georgianPanel.style.display = state.georgianEnabled ? 'block' : 'none';

    var leadPanel = document.getElementById('gc8-lead-config');
    if (leadPanel) leadPanel.style.display = state.leadEnabled ? 'block' : 'none';

    var catflapConfig = document.getElementById('gc8-catflap-config');
    if (catflapConfig) catflapConfig.style.display = state.catflap ? 'block' : 'none';

    // Letterbox visible for doors or conservatory doors
    var letterboxRow = document.getElementById('gc8-letterbox-row');
    if (letterboxRow) {
      var showLetterbox = state.installType === 'door' || (state.installType === 'conservatory' && state.conservatoryType === 'door');
      letterboxRow.style.display = showLetterbox ? 'flex' : 'none';
    }

    // Clear dimension error states
    var wEl = document.getElementById('gc8-width');
    var hEl = document.getElementById('gc8-height');
    if (wEl) wEl.classList.remove('pr-input--error');
    if (hEl) hEl.classList.remove('pr-input--error');
  }

  function syncRadio(fieldName, value) {
    if (!value) return;
    var els = document.querySelectorAll('[data-gc8-field="' + fieldName + '"]');
    for (var i = 0; i < els.length; i++) {
      if (els[i].type === 'radio') {
        els[i].checked = (els[i].value === value);
      } else if (els[i].tagName === 'SELECT') {
        els[i].value = value;
      }
    }
  }

  function syncNumber(fieldName, value) {
    var el = document.querySelector('[data-gc8-field="' + fieldName + '"]');
    if (el && value !== undefined) el.value = value;
  }

  function syncCheckbox(fieldName, value) {
    var el = document.querySelector('[data-gc8-field="' + fieldName + '"]');
    if (el) el.checked = !!value;
  }


  /* ═══════════════════════════════════════════════════════════════════════════
   * BOOT
   * ═══════════════════════════════════════════════════════════════════════════ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
